import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  doc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Camera, 
  Video, 
  Upload, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Package,
  ClipboardList,
  FileText,
  Save,
  Clock,
  ArrowLeft,
  Search,
  Filter,
  Calendar,
  User,
  Building,
  Truck,
  Scale,
  Eye,
  Download,
  X,
  Plus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  WifiOff,
  HardDrive,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Warehouse
} from 'lucide-react';
import PalletsRecebimentoModal from './armazenamento/PalletsRecebimentoModal';
import { saveDraft as saveReceivingDraft, getDrafts as getReceivingDrafts, deleteDraft as deleteReceivingDraft } from '../services/offlineReceivingsService';
import { filterItemsBySearch } from '../utils/textSanitize';
import { insumoCorrespondeTermo } from '../utils/insumoBusca';
import { formatQuantidade, formatQuantidadeComUnidade } from '../utils/numberFormat';
import DateInputPt from './ui/DateInputPt';

// ✅ NOVO: Separar dados básicos (comuns a todas as matérias-primas) e específicos
const toYmdLocal = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseAnyDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw?.toDate === 'function') return raw.toDate();
  if (raw?._seconds != null) return new Date(raw._seconds * 1000);
  if (raw?.seconds != null) return new Date(raw.seconds * 1000);
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 12, 0, 0);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getDataRecebimentoRaw = (recebimento) =>
  recebimento?.data_recebimento ?? recebimento?.criado_em ?? null;

const formatDataRecebimento = (recebimento, { withTime = false } = {}) => {
  try {
    const date = parseAnyDate(getDataRecebimentoRaw(recebimento));
    if (!date) return 'N/A';
    if (withTime) {
      return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
    return date.toLocaleDateString('pt-BR');
  } catch {
    return 'N/A';
  }
};

/** Data de recebimento anterior ao dia do registro no sistema */
const isRecebimentoDataRetroativa = (recebimento) => {
  if (recebimento?.data_retroativa === true) return true;
  if (recebimento?.data_retroativa === false) return false;
  const dataRecv = parseAnyDate(getDataRecebimentoRaw(recebimento));
  const criado = parseAnyDate(recebimento?.criado_em);
  if (!dataRecv || !criado) return false;
  const dayRecv = new Date(dataRecv.getFullYear(), dataRecv.getMonth(), dataRecv.getDate()).getTime();
  const dayCriado = new Date(criado.getFullYear(), criado.getMonth(), criado.getDate()).getTime();
  return dayRecv < dayCriado;
};

/** Categorias de foto do recebimento (mín. 1 foto nas obrigatórias). */
const FOTOS_RECEBIMENTO_CATEGORIAS = [
  { id: 'nota_fiscal', label: 'Nota Fiscal', obrigatoria: true },
  { id: 'laudo_qualidade', label: 'Laudo de Qualidade', obrigatoria: true },
  { id: 'rotulo', label: 'Rótulo', obrigatoria: true },
  { id: 'placa_lacre', label: 'Placa do caminhão / Lacre', obrigatoria: true },
  { id: 'carga', label: 'Carga', obrigatoria: true },
  { id: 'outras', label: 'Fotos adicionais', obrigatoria: false }
];

const createEmptyFotosPorCategoria = () =>
  Object.fromEntries(FOTOS_RECEBIMENTO_CATEGORIAS.map((c) => [c.id, []]));

const normalizeFotosPorCategoria = (raw, fotosGeraisLegado = []) => {
  const base = createEmptyFotosPorCategoria();
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    FOTOS_RECEBIMENTO_CATEGORIAS.forEach((c) => {
      if (Array.isArray(raw[c.id])) base[c.id] = raw[c.id];
    });
    return base;
  }
  if (Array.isArray(fotosGeraisLegado) && fotosGeraisLegado.length > 0) {
    base.outras = fotosGeraisLegado.map((f) =>
      typeof f === 'object' && f !== null ? { ...f, categoria: f.categoria || 'outras' } : f
    );
  }
  return base;
};

const flattenFotosPorCategoria = (fotosPorCategoria) => {
  const out = [];
  FOTOS_RECEBIMENTO_CATEGORIAS.forEach((c) => {
    (fotosPorCategoria?.[c.id] || []).forEach((f) => {
      if (!f) return;
      out.push(typeof f === 'object' ? { ...f, categoria: c.id } : { url: String(f), categoria: c.id });
    });
  });
  return out;
};

/** Retorna labels das categorias obrigatórias sem foto. */
const validarFotosObrigatoriasRecebimento = (fotosPorCategoria) =>
  FOTOS_RECEBIMENTO_CATEGORIAS.filter((c) => c.obrigatoria)
    .filter((c) => !((fotosPorCategoria?.[c.id] || []).length > 0))
    .map((c) => c.label);

const labelCategoriaFoto = (id) =>
  FOTOS_RECEBIMENTO_CATEGORIAS.find((c) => c.id === id)?.label || id || 'Sem categoria';

const INITIAL_BASIC_DATA = {
  fornecedor: '',
  fornecedor_id: '', // ✅ NOVO: ID do fornecedor selecionado
  dataRecebimento: '', // YYYY-MM-DD — data real do recebimento
  numeroNotaFiscal: '',
  numeroDcpoa: '',
  dcpoaNaoSeAplica: false,
  numeroLacre: '',
  lacreNaoSeAplica: false,
  placaCaminhao: '',
  condicoesTransporte: '',
  temperaturaCarga: '',
  fotos_por_categoria: createEmptyFotosPorCategoria(),
  videos_gerais: []
};

const INITIAL_PRODUCT_DATA = {
  produto_id: '',
  produto_nome: '',
  lote_fornecedor: '',
  lotesQuantidades: {}, // { [lote]: '1.000,00' } quando há múltiplos lotes
  quantidade: '',
  unidade: 'Kg',
  dataFabricacao: '',
  dataValidade: '',
  observacoes: '',
  parametros: {},
  fotos_especificas: [], // ✅ NOVO: Fotos/vídeos específicos da matéria-prima
  videos_especificas: []
};

// ✅ MANTIDO: Para compatibilidade com código existente
const INITIAL_FORM_STATE = {
  ...INITIAL_BASIC_DATA,
  ...INITIAL_PRODUCT_DATA,
  // Manter campos antigos para compatibilidade
  lote_fornecedor: '',
  quantidade: '',
  unidade: 'Kg',
  dataFabricacao: '',
  dataValidade: '',
  observacoes: '',
  parametros: {},
  fotos: [],
  videos: []
};

const deepClone = (value) => (
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value))
);

const createInitialFormData = () => ({
  ...deepClone(INITIAL_FORM_STATE),
  dataRecebimento: toYmdLocal(new Date())
});
const createInitialBasicData = () => ({
  ...deepClone(INITIAL_BASIC_DATA),
  dataRecebimento: toYmdLocal(new Date()),
  fotos_por_categoria: createEmptyFotosPorCategoria()
});
const createInitialProductData = () => deepClone(INITIAL_PRODUCT_DATA);

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const dataUrlToFile = (dataUrl, fileName, mimeType) => {
  const arr = dataUrl.split(',');
  const mime = mimeType || arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
};

const ReceivingsPage = () => {
  const { user, userData, companyId, getEmpresaId, isRole, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Log inicial para debug
  useEffect(() => {
    console.log('🔍 ReceivingsPage: Componente renderizado', {
      companyId,
      hasUser: !!user,
      userRole: userData?.funcao
    });
  }, []);
  
  // Estados principais
  const [currentStep, setCurrentStep] = useState(1); // 1: Seleção Fornecedor, 2: Dados Básicos + Cards, 3: Dados Específicos MP, 4: Validação, 5: Decisão
  const [selectedProduct, setSelectedProduct] = useState(null); // ✅ MANTIDO: Para compatibilidade
  const [selectedSupplier, setSelectedSupplier] = useState(null); // ✅ NOVO: Fornecedor selecionado
  const [supplierProducts, setSupplierProducts] = useState([]); // ✅ NOVO: Matérias-primas do fornecedor
  const [processedProducts, setProcessedProducts] = useState({}); // ✅ NOVO: Tracking de matérias-primas processadas { produto_id: { count: number, totalVolume: number, unit: string } }
  const [currentProductData, setCurrentProductData] = useState(() => createInitialProductData()); // ✅ NOVO: Dados específicos da MP atual
  const [basicData, setBasicData] = useState(() => createInitialBasicData()); // ✅ NOVO: Dados básicos do recebimento
  const [showProductModal, setShowProductModal] = useState(false); // ✅ NOVO: Modal para dados específicos da MP
  const [produtos, setProdutos] = useState([]);
  const RECEBIMENTOS_PAGE_SIZE = 100;
  const [recebimentos, setRecebimentos] = useState([]);
  const [recebimentosPagination, setRecebimentosPagination] = useState({ page: 1, limit: RECEBIMENTOS_PAGE_SIZE, total: 0, totalPages: 0 });
  const [allFornecedores, setAllFornecedores] = useState([]); // ✅ Todos os fornecedores aprovados
  const [fornecedores, setFornecedores] = useState([]); // ✅ Fornecedores filtrados pelo produto selecionado
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingReceiving, setViewingReceiving] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editViewQuantidade, setEditViewQuantidade] = useState('');
  const [editViewUnidade, setEditViewUnidade] = useState('Kg');
  const [savingViewQuantidade, setSavingViewQuantidade] = useState(false);
  const [editViewDataRecebimento, setEditViewDataRecebimento] = useState('');
  const [savingViewDataRecebimento, setSavingViewDataRecebimento] = useState(false);
  const [showFinalDecisionModal, setShowFinalDecisionModal] = useState(false);
  const [receivingForFinalDecision, setReceivingForFinalDecision] = useState(null);
  const [finalDecision, setFinalDecision] = useState('');
  const [finalObservations, setFinalObservations] = useState('');
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [pendingReceivingId, setPendingReceivingId] = useState(() => location.state?.receivingId || null);
  const [moduloArmazenamento, setModuloArmazenamento] = useState(false);
  const [palletRecebimento, setPalletRecebimento] = useState(null);
  useEffect(() => {
    let cancel = false;
    const empresaAtual = companyId || getEmpresaId();
    if (!empresaAtual || empresaAtual === 'default') return undefined;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getStatusModuloArmazenamento');
        const r = await fn({ empresa_id: empresaAtual });
        if (!cancel) setModuloArmazenamento(!!r.data?.data?.ativo);
      } catch {
        if (!cancel) setModuloArmazenamento(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [companyId]);

  const recebimentoAceitoParaPallet = (recebimento) =>
    ['aceitar', 'aceito', 'aprovado_qualidade'].includes(String(recebimento?.decisao_final || '').toLowerCase());

  const abrirPalletsRecebimento = (recebimento) => {
    if (!moduloArmazenamento || !recebimento?.id) return;
    setPalletRecebimento(recebimento);
  };

  // ✅ NOVO: Se a navegação veio com um recebimento específico, guardar o ID e limpar o estado da rota
  useEffect(() => {
    if (location.state && location.state.receivingId) {
      setPendingReceivingId(location.state.receivingId);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  // ✅ NOVO: Abrir automaticamente o recebimento solicitado (visualização ou decisão final)
  useEffect(() => {
    if (!pendingReceivingId || recebimentos.length === 0) return;

    const targetReceiving = recebimentos.find((rec) => rec.id === pendingReceivingId);
    if (!targetReceiving) return;

    if (targetReceiving.decisao_final === 'aprovado_qualidade' || targetReceiving.decisao_final === 'rejeitado_qualidade') {
      setReceivingForFinalDecision(targetReceiving);
      setFinalDecision('');
      setFinalObservations('');
      setShowFinalDecisionModal(true);
    } else {
      setViewingReceiving(targetReceiving);
      const qtd = targetReceiving?.quantidade_recebida;
      setEditViewQuantidade(
        qtd != null && qtd !== ''
          ? Number(qtd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : ''
      );
      setEditViewUnidade(targetReceiving?.unidade_medida || 'Kg');
      const dateRecv = parseAnyDate(getDataRecebimentoRaw(targetReceiving));
      setEditViewDataRecebimento(dateRecv ? toYmdLocal(dateRecv) : toYmdLocal(new Date()));
      setShowViewModal(true);
    }

    setPendingReceivingId(null);
  }, [pendingReceivingId, recebimentos]);
  
  // Estados do formulário de recebimento
  const [formData, setFormData] = useState(createInitialFormData());
  
  // Estados de validação (para fluxo antigo)
  const [validationResults, setValidationResults] = useState({});
  const [conformityScore, setConformityScore] = useState(0);
  const [recommendation, setRecommendation] = useState('');
  
  // ✅ NOVO: Estados de validação para o novo fluxo (por matéria-prima)
  const [productValidationResults, setProductValidationResults] = useState({});
  const [productConformityScore, setProductConformityScore] = useState(0);
  const [productRecommendation, setProductRecommendation] = useState(null);
  const [showProductDecisionModal, setShowProductDecisionModal] = useState(false);
  
  // Estados de filtros e busca
  const [searchTermHistorico, setSearchTermHistorico] = useState('');
  const [searchTermProduto, setSearchTermProduto] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterTipoFornecedor, setFilterTipoFornecedor] = useState('todos');
  const [filterDateInicio, setFilterDateInicio] = useState('');
  const [filterDateFim, setFilterDateFim] = useState('');
  
  // Estado para controle de abas
  const [activeTab, setActiveTab] = useState('novo'); // 'novo' ou 'historico'
  

  
  // Carregar produtos da empresa
  useEffect(() => {
    if (user && companyId && companyId !== 'default') {
      loadProdutos();
      loadRecebimentos(1, { search: '', status: 'todos', data: '' });
      loadFornecedores(); // ✅ NOVO: Carregar fornecedores
    } else {
      // Se não temos user válido, não devemos estar carregando
      if (!user || !companyId || companyId === 'default') {
        setLoading(false);
      }
    }
  }, [user, companyId]);

  // Histórico: busca/status/tipo/data no servidor (cobre itens fora da 1ª página)
  useEffect(() => {
    if (!user || !companyId || companyId === 'default') return;
    if (activeTab !== 'historico') return;
    const t = setTimeout(() => {
      loadRecebimentos(1, {
        search: searchTermHistorico,
        status: filterStatus,
        tipoFornecedor: filterTipoFornecedor,
        dataInicio: filterDateInicio,
        dataFim: filterDateFim
      });
    }, 400);
    return () => clearTimeout(t);
  }, [activeTab, searchTermHistorico, filterStatus, filterTipoFornecedor, filterDateInicio, filterDateFim, user, companyId]);

  const refreshDrafts = useCallback(async () => {
    try {
      setDraftsLoading(true);
      const storedDrafts = await getReceivingDrafts();
      const empresaContextId = companyId || getEmpresaId();
      const filtered = storedDrafts.filter((draft) => !draft.empresaId || draft.empresaId === empresaContextId);
      setDrafts(filtered);
    } catch (error) {
      console.error('Erro ao carregar rascunhos offline:', error);
    } finally {
      setDraftsLoading(false);
    }
  }, [companyId, getEmpresaId]);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      refreshDrafts();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshDrafts]);

  const loadProdutos = async () => {
    try {
      setLoading(true);
      
      // ✅ CORREÇÃO: Usar httpsCallable como na página de produtos
      const listProducts = httpsCallable(functions, 'listProducts');
      
      const result = await listProducts({
        companyId: companyId,
        page: 1,
        limit: 1000
      });

      if (result.data?.success) {
        const produtosList = result.data.data.products;
        setProdutos(produtosList);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar produtos');
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setError('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  // Helper para converter timestamps do Firebase
  const convertFirebaseTimestamps = (obj) => {
    if (!obj) return obj;
    
    const converted = { ...obj };
    
    // Converter campos de data conhecidos
    const dateFields = ['data_fabricacao', 'data_validade', 'data_recebimento', 'criado_em', 'atualizado_em', 'data_analise'];
    
    dateFields.forEach(field => {
      if (converted[field]) {
        // Se for um objeto com _seconds (formato Firebase Functions)
        if (converted[field]._seconds) {
          converted[field] = new Date(converted[field]._seconds * 1000);
        }
        // Se for string ISO
        else if (typeof converted[field] === 'string') {
          converted[field] = new Date(converted[field]);
        }
      }
    });
    
    return converted;
  };

  const loadRecebimentos = async (
    page = 1,
    filtros = {
      search: searchTermHistorico,
      status: filterStatus,
      tipoFornecedor: filterTipoFornecedor,
      dataInicio: filterDateInicio,
      dataFim: filterDateFim
    }
  ) => {
    try {
      const listReceivings = httpsCallable(functions, 'listReceivings');
      const search = String(filtros.search || '').trim();
      const statusFiltro = filtros.status && filtros.status !== 'todos' ? filtros.status : undefined;
      const tipoFiltro =
        filtros.tipoFornecedor && filtros.tipoFornecedor !== 'todos'
          ? filtros.tipoFornecedor
          : undefined;
      const dataInicio = String(filtros.dataInicio || '').trim() || undefined;
      const dataFim = String(filtros.dataFim || '').trim() || undefined;

      const result = await listReceivings({
        empresa_id: isRole('superadmin') ? undefined : (isRole('fornecedor') ? undefined : companyId),
        page,
        limit: RECEBIMENTOS_PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(statusFiltro ? { decisao_final: statusFiltro } : {}),
        ...(tipoFiltro ? { tipo_insumo: tipoFiltro } : {}),
        ...(dataInicio ? { data_recebimento_inicio: dataInicio } : {}),
        ...(dataFim ? { data_recebimento_fim: dataFim } : {}),
      });

      if (result.data?.success) {
        const recebimentosList = result.data.data.recebimentos.map(rec => convertFirebaseTimestamps(rec));
        setRecebimentos(recebimentosList);
        const pag = result.data.data.pagination;
        if (pag) {
          setRecebimentosPagination({ page: pag.page, limit: pag.limit, total: pag.total, totalPages: pag.totalPages });
        }
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar recebimentos');
      }
    } catch (error) {
      console.error('Erro ao carregar recebimentos:', error);
      setError('Erro ao carregar recebimentos');
    }
  };

  // ✅ NOVA FUNÇÃO: Carregar produtos de um fornecedor específico
  const loadSupplierProducts = async (supplierId) => {
    try {
      if (!supplierId || String(supplierId).trim() === '') {
        console.warn('Carregamento de produtos: fornecedor sem ID de documento');
        setSupplierProducts([]);
        return;
      }
      // Evita listar todo o catálogo da empresa se o ID do fornecedor estiver errado (ex.: legado em listSuppliers)
      if (companyId && String(supplierId).trim() === String(companyId).trim()) {
        console.error(
          'Recebimentos: ID do fornecedor é igual ao ID da empresa; abortando listagem de insumos para não exibir o catálogo inteiro.'
        );
        setSupplierProducts([]);
        return;
      }
      console.log('🔍 Carregando catálogo do fornecedor (listProdutosRecebimentoPorFornecedor):', supplierId);
      const empresaId = getEmpresaId();
      if (!empresaId || empresaId === 'default') {
        console.warn('Recebimentos: empresa_id inválido');
        setSupplierProducts([]);
        return;
      }
      const fn = httpsCallable(functions, 'listProdutosRecebimentoPorFornecedor');
      const result = await fn({
        fornecedor_id: supplierId,
        empresa_id: empresaId,
        limit: 1500
      });

      if (result.data?.success) {
        const produtos = result.data.data.products || result.data.data.items || [];
        console.log('✅ Produtos do fornecedor carregados:', produtos.length);
        setSupplierProducts(produtos);
      } else {
        console.warn('⚠️ Nenhum produto encontrado para o fornecedor');
        setSupplierProducts([]);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos do fornecedor:', error);
      setSupplierProducts([]);
    }
  };

  // ✅ NOVA FUNÇÃO: Carregar fornecedores aprovados
  const loadFornecedores = async () => {
    try {
      const listSuppliers = httpsCallable(functions, 'listSuppliers');
      
      const result = await listSuppliers({
        id_empresa: companyId,
        status: 'aprovado', // ✅ Apenas fornecedores aprovados
        page: 1,
        limit: 1000
      });

      if (result.data?.success) {
        const fornecedoresList = result.data.data.items || [];
        console.log('✅ Fornecedores carregados:', fornecedoresList.length);
        console.log('🔍 IDs dos fornecedores:', fornecedoresList.map((f) => f.id));
        setAllFornecedores(fornecedoresList);
        // Inicialmente, não filtrar (mostrar todos até que um produto seja selecionado)
        setFornecedores(fornecedoresList);
      } else {
        console.warn('⚠️ Nenhum fornecedor aprovado encontrado');
        setAllFornecedores([]);
        setFornecedores([]);
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      setAllFornecedores([]);
      setFornecedores([]);
    }
  };

  // ✅ NOVA FUNÇÃO: Filtrar fornecedores pelo produto selecionado (usando Cloud Function)
  const filterFornecedoresByProduct = async (product) => {
    if (!product) {
      // Se não há produto selecionado, mostrar todos os fornecedores
      setFornecedores(allFornecedores);
      return;
    }

    try {
      console.log('🔍 Filtrando fornecedores para o produto:', product.nome, product.id);
      console.log('🔍 Dados do produto:', { 
        produto_base_id: product.produto_base_id, 
        codigo: product.codigo,
        codigo_produto: product.codigo_produto,
        id: product.id
      });
      
      // ✅ USAR CLOUD FUNCTION para buscar fornecedores
      console.log('🔍 Chamando Cloud Function getSuppliersByProduct...');
      const getSuppliersByProduct = httpsCallable(functions, 'getSuppliersByProduct');
      
      console.log('🔍 Parâmetros enviados:', {
        produto_id: product.id,
        produto_base_id: product.produto_base_id,
        codigo: product.codigo,
        codigo_produto: product.codigo_produto,
        id_empresa: companyId
      });
      
      const result = await getSuppliersByProduct({
        produto_id: product.id,
        produto_base_id: product.produto_base_id,
        codigo: product.codigo,
        codigo_produto: product.codigo_produto,
        id_empresa: companyId
      });
      
      console.log('✅ Resultado da Cloud Function recebido:', result);

      if (result.data?.success) {
        const suppliersFromFunction = result.data.data.suppliers || [];
        console.log('✅ Fornecedores retornados pela função:', suppliersFromFunction.length);
        
        // Criar um Set com os IDs dos fornecedores retornados
        const supplierIdsFromFunction = new Set(suppliersFromFunction.map((s) => s.id));
        
        // Filtrar allFornecedores para incluir apenas os que foram retornados
        const fornecedoresFiltrados = allFornecedores.filter(fornecedor => 
          supplierIdsFromFunction.has(fornecedor.id)
        );

        console.log('✅ Fornecedores filtrados:', fornecedoresFiltrados.length, 'de', allFornecedores.length);
        
        if (fornecedoresFiltrados.length === 0) {
          console.warn('⚠️ Nenhum fornecedor encontrado com este produto. Mostrando todos os fornecedores.');
          // Se não encontrou nenhum, mostrar todos (pode ser que o produto não esteja associado ainda)
          setFornecedores(allFornecedores);
        } else {
          setFornecedores(fornecedoresFiltrados);
        }
      } else {
        console.warn('⚠️ Função retornou sem sucesso. Mostrando todos os fornecedores.');
        setFornecedores(allFornecedores);
      }
    } catch (error) {
      console.error('❌ Erro ao filtrar fornecedores:', error);
      // Em caso de erro, mostrar todos os fornecedores
      setFornecedores(allFornecedores);
    }
  };

  // ✅ Filtrar fornecedores quando um produto é selecionado
  useEffect(() => {
    console.log('🔍 useEffect FILTRO - Executado!', {
      selectedProduct: selectedProduct ? {
        nome: selectedProduct.nome,
        id: selectedProduct.id,
        codigo: selectedProduct.codigo,
        produto_base_id: selectedProduct.produto_base_id
      } : null,
      allFornecedoresCount: allFornecedores.length,
      fornecedoresCount: fornecedores.length,
      companyId
    });
    
    if (selectedProduct && allFornecedores.length > 0) {
      console.log('🔍 Condição satisfeita: produto selecionado E fornecedores carregados');
      console.log('🔍 Chamando filterFornecedoresByProduct...');
      filterFornecedoresByProduct(selectedProduct);
    } else if (!selectedProduct) {
      // Se não há produto selecionado, mostrar todos
      console.log('🔍 Sem produto selecionado, mostrando todos os fornecedores');
      setFornecedores(allFornecedores);
    } else if (selectedProduct && allFornecedores.length === 0) {
      console.warn('⚠️ Produto selecionado mas fornecedores ainda não carregados');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, allFornecedores, companyId]);

  // ✅ NOVO: Funções helper para conversão de vírgula/ponto (padrão brasileiro)
  const formatarNumeroParaExibicao = (valor) => {
    if (!valor && valor !== 0) return '';
    // Converter ponto para vírgula para exibição
    return String(valor).replace('.', ',');
  };

  const formatarNumeroParaCalculo = (valor) => {
    if (!valor && valor !== 0) return '';
    // Remove separador de milhar (.) e usa ponto decimal
    return String(valor).replace(/\./g, '').replace(',', '.');
  };

  /** Máximo permitido: 999.999,99 (6 dígitos inteiros + 2 decimais). */
  const QUANTIDADE_MAXIMA = 999999.99;

  /** Quantidade BR: milhar com "." e até 2 casas com "," enquanto digita. */
  const formatarQuantidadeDigitando = (valor) => {
    if (valor == null || valor === '') return '';
    const s = String(valor).replace(/[^\d,]/g, '');
    const partes = s.split(',');
    let intPart = (partes[0] || '').replace(/^0+(?=\d)/, '').slice(0, 6);
    const temVirgula = partes.length > 1;
    const decPart = temVirgula ? partes.slice(1).join('').slice(0, 2) : '';
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if (temVirgula) return `${intPart},${decPart}`;
    return intPart;
  };

  /** Ao sair do campo, fixa exatamente 2 casas decimais (pt-BR) e limita ao máximo. */
  const formatarQuantidadeBlur = (valor) => {
    if (valor === '' || valor == null) return '';
    let n = parseFloat(formatarNumeroParaCalculo(valor));
    if (!Number.isFinite(n)) return '';
    if (n > QUANTIDADE_MAXIMA) n = QUANTIDADE_MAXIMA;
    if (n < 0) n = 0;
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleNumeroInput = (valor, callback) => {
    // Permitir apenas números, vírgula e ponto
    let valorLimpo = valor.replace(/[^\d,.-]/g, '');
    
    // Converter ponto para vírgula (padrão brasileiro)
    valorLimpo = valorLimpo.replace(/\./g, ',');
    
    // Garantir apenas uma vírgula
    const partes = valorLimpo.split(',');
    let valorFormatado = partes[0] || '';
    if (partes.length > 1) {
      // Juntar apenas a primeira parte decimal
      valorFormatado += ',' + partes.slice(1).join('').substring(0, 10); // Limitar casas decimais
    }
    
    callback(valorFormatado);
  };

  const handleQuantidadeInput = (valor, callback) => {
    callback(formatarQuantidadeDigitando(valor));
  };

  // ✅ NOVO: Funções para máscara e validação de data (DD/MM/YYYY)
  const aplicarMascaraData = (valor) => {
    // Remover tudo que não é número
    const numeros = valor.replace(/\D/g, '');
    
    // Aplicar máscara DD/MM/YYYY
    if (numeros.length <= 2) {
      return numeros;
    } else if (numeros.length <= 4) {
      return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
    } else {
      return `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4, 8)}`;
    }
  };

  const validarData = (dataStr) => {
    if (!dataStr || dataStr.length < 10) return { valida: false, mensagem: 'Data incompleta' };
    
    const partes = dataStr.split('/');
    if (partes.length !== 3) return { valida: false, mensagem: 'Formato inválido' };
    
    const dia = parseInt(partes[0], 10);
    const mes = parseInt(partes[1], 10);
    const ano = parseInt(partes[2], 10);
    
    if (isNaN(dia) || isNaN(mes) || isNaN(ano)) {
      return { valida: false, mensagem: 'Data inválida' };
    }
    
    if (dia < 1 || dia > 31) {
      return { valida: false, mensagem: 'Dia inválido (1-31)' };
    }
    
    if (mes < 1 || mes > 12) {
      return { valida: false, mensagem: 'Mês inválido (1-12)' };
    }
    
    if (ano < 1900 || ano > 2100) {
      return { valida: false, mensagem: 'Ano inválido' };
    }
    
    // Validar se a data realmente existe (ex: 31/02 não existe)
    const data = new Date(ano, mes - 1, dia);
    if (data.getDate() !== dia || data.getMonth() !== mes - 1 || data.getFullYear() !== ano) {
      return { valida: false, mensagem: 'Data não existe' };
    }
    
    return { valida: true, dia, mes, ano };
  };

  const converterDataParaISO = (dataDDMMYYYY) => {
    // Converter DD/MM/YYYY para YYYY-MM-DD (formato ISO)
    if (!dataDDMMYYYY || dataDDMMYYYY.length < 10) return '';
    
    const partes = dataDDMMYYYY.split('/');
    if (partes.length !== 3) return '';
    
    const dia = partes[0].padStart(2, '0');
    const mes = partes[1].padStart(2, '0');
    const ano = partes[2];
    
    return `${ano}-${mes}-${dia}`;
  };

  const converterDataDeISO = (dataISO) => {
    // Converter YYYY-MM-DD para DD/MM/YYYY (para exibição)
    if (!dataISO) return '';
    
    // Se já está no formato DD/MM/YYYY, retornar como está
    if (dataISO.includes('/')) return dataISO;
    
    // Converter de YYYY-MM-DD
    const partes = dataISO.split('-');
    if (partes.length !== 3) return dataISO;
    
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  };

  const handleDataInput = (valor, callback) => {
    // Aplicar máscara durante a digitação
    const valorComMascara = aplicarMascaraData(valor);
    callback(valorComMascara);
  };

  // Validar parâmetros em tempo real
  const validateParameter = (parametro, valor) => {
    if (!parametro) return { status: 'pending', message: 'Aguardando valor' };

    // ✅ NOVO: Detectar se é parâmetro sensorial (IGUAL À VERSÃO ANTIGA)
    const isSensorial = parametro.tipo_limite === 'descritivo' || 
                        parametro.padrao?.toLowerCase() === 'sensorial' ||
                        (!parametro.valor_minimo && !parametro.valor_maximo && 
                         !parametro.limite_minimo && !parametro.limite_maximo &&
                         parametro.tipo_limite === 'descritivo');

    if (isSensorial) {
      // ✅ Validação para parâmetros sensoriais
      if (!valor) {
        const obrigatorio = parametro.obrigatorio ?? true;
        return {
          status: obrigatorio ? 'error' : 'warning',
          message: obrigatorio ? 'Parâmetro obrigatório' : 'Aguardando avaliação'
        };
      }

      // Se valor é objeto (formato novo para sensoriais)
      if (typeof valor === 'object') {
        if (valor.conforme === true) {
          return { status: 'success', message: 'Conforme com o esperado' };
        } else if (valor.descricao && valor.descricao.trim()) {
          // ✅ CORREÇÃO: Não conforme deve ser tratado como erro (bloqueia aprovação)
          const obrigatorio = parametro.obrigatorio ?? true;
          return { 
            status: obrigatorio ? 'error' : 'warning', 
            message: 'Não conforme - descrição registrada' 
          };
        } else {
          return { status: 'error', message: 'Descreva o estado observado' };
        }
      }

      // Se valor é string (formato antigo ou texto livre)
      if (typeof valor === 'string' && valor.trim()) {
        if (valor.toLowerCase() === 'conforme') {
          return { status: 'success', message: 'Conforme com o esperado' };
        }
        return { status: 'warning', message: 'Descrição registrada' };
      }

      return { status: 'error', message: 'Avaliação necessária' };
    }

    // ✅ Validação para parâmetros numéricos (comportamento original)
    if (!valor && valor !== 0 && valor !== '0') {
      const obrigatorio = parametro.obrigatorio ?? true;
      return {
        status: obrigatorio ? 'error' : 'warning',
        message: obrigatorio ? 'Valor obrigatório' : 'Aguardando valor'
      };
    }

    // ✅ CORREÇÃO: Converter vírgula para ponto antes de parseFloat
    const valorParaCalculo = formatarNumeroParaCalculo(valor);
    const numericValue = parseFloat(valorParaCalculo);
    if (isNaN(numericValue)) return { status: 'error', message: 'Valor inválido' };

    // ✅ CORREÇÃO: Aceitar ambas as estruturas (valor_* e limite_*)
    const valor_minimo = parametro.valor_minimo ?? parametro.limite_minimo;
    const valor_maximo = parametro.valor_maximo ?? parametro.limite_maximo;
    const obrigatorio = parametro.obrigatorio ?? true; // Default para obrigatório
    
    // ✅ CORREÇÃO ESPECIAL: Tratar "Ausente" como limite máximo zero
    const maximoParaComparacao = valor_maximo === "Ausente" ? 0 : valor_maximo;
    
    if (valor_minimo !== null && valor_minimo !== undefined && numericValue < valor_minimo) {
      return {
        status: obrigatorio ? 'error' : 'warning',
        message: `Abaixo do limite mínimo (${valor_minimo})`
      };
    }
    
    if (maximoParaComparacao !== null && maximoParaComparacao !== undefined && numericValue > maximoParaComparacao) {
      const mensagem = valor_maximo === "Ausente" 
        ? "Salmonella detectada - deve estar ausente" 
        : `Acima do limite máximo (${valor_maximo})`;
      
      return {
        status: obrigatorio ? 'error' : 'warning',
        message: mensagem
      };
    }

    return { status: 'success', message: 'Dentro dos parâmetros' };
  };

  // Calcular conformidade geral
  const calculateConformity = () => {
    const parametros = selectedProduct?.parametros_qualidade || selectedProduct?.parametros; // ✅ NORMALIZADO
    if (!parametros) return 0;

    const totalParams = parametros.length;
    let conformeParams = 0;

    parametros.forEach(param => {
      const valor = formData.parametros[param.nome];
      if (valor) {
        // ✅ NOVO: Para parâmetros sensoriais, considerar conforme se marcado como conforme
        const isSensorial = param.tipo_limite === 'descritivo' || 
                           param.padrao === 'Sensorial' ||
                           (!param.valor_minimo && !param.valor_maximo && 
                            !param.limite_minimo && !param.limite_maximo &&
                            param.tipo_limite === 'descritivo');
        
        if (isSensorial && typeof valor === 'object') {
          if (valor.conforme === true) {
            conformeParams++;
          }
        } else {
          const validation = validateParameter(param, valor);
          if (validation.status === 'success') {
            conformeParams++;
          }
        }
      }
    });

    return totalParams > 0 ? Math.round((conformeParams / totalParams) * 100) : 0;
  };

  // Gerar recomendação baseada na validação
  const generateRecommendation = () => {
    const score = calculateConformity();
    const hasErrors = Object.values(validationResults).some(r => r.status === 'error');
    const hasWarnings = Object.values(validationResults).some(r => r.status === 'warning');

    if (hasErrors) {
      return {
        action: 'REJEITAR',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        message: 'Produto não conforme. Parâmetros críticos fora dos limites.',
        podeAceitar: false // ✅ NOVO: Bloquear aceitação
      };
    }

    if (hasWarnings && score < 80) {
      return {
        action: 'ANALISAR',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        message: 'Produto requer análise adicional. Alguns parâmetros fora dos limites.',
        podeAceitar: false // ✅ NOVO: Bloquear aceitação
      };
    }

    if (score >= 90) {
      return {
        action: 'ACEITAR',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        message: 'Produto conforme. Todos os parâmetros dentro dos limites.',
        podeAceitar: true
      };
    }

    return {
      action: 'ANALISAR',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      message: 'Produto requer análise. Conformidade parcial.',
      podeAceitar: false // ✅ NOVO: Bloquear aceitação
    };
  };

  // Atualizar validação quando parâmetros mudam
  useEffect(() => {
    const parametros = selectedProduct?.parametros_qualidade || selectedProduct?.parametros; // ✅ NORMALIZADO
    if (parametros) {
      const results = {};
      parametros.forEach(param => {
        const valor = formData.parametros[param.nome];
        if (valor) {
          results[param.nome] = validateParameter(param, valor);
        }
      });
      setValidationResults(results);
      setConformityScore(calculateConformity());
    }
  }, [formData.parametros, selectedProduct]);

  // Atualizar recomendação quando validação muda
  useEffect(() => {
    if (Object.keys(validationResults).length > 0) {
      const rec = generateRecommendation();
      setRecommendation(rec);
    }
  }, [validationResults, conformityScore]);

  // ✅ NOVO: Inicializar parâmetros sensoriais quando produto é selecionado
  useEffect(() => {
    if (currentProductData.produto_id && supplierProducts.length > 0) {
      const produtoCompleto = supplierProducts.find(p => p.id === currentProductData.produto_id);
      const parametros = produtoCompleto?.parametros_qualidade || produtoCompleto?.parametros || [];
      
      if (parametros.length > 0) {
        // Inicializar parâmetros sensoriais que ainda não foram inicializados
        const parametrosToInit = {};
        let needsUpdate = false;
        
        parametros.forEach(param => {
          // ✅ Detectar se é parâmetro sensorial (IGUAL À VERSÃO ANTIGA)
          const isSensorial = param.tipo_limite === 'descritivo' || 
                             param.padrao?.toLowerCase() === 'sensorial' ||
                             (!param.valor_minimo && !param.valor_maximo && 
                              !param.limite_minimo && !param.limite_maximo &&
                              param.tipo_limite === 'descritivo');
          
          // Se for sensorial e ainda não foi inicializado, inicializar
          if (isSensorial && !currentProductData.parametros[param.nome]) {
            parametrosToInit[param.nome] = { conforme: false, descricao: '' };
            needsUpdate = true;
          }
        });
        
        if (needsUpdate) {
          setCurrentProductData(prev => ({
            ...prev,
            parametros: {
              ...prev.parametros,
              ...parametrosToInit
            }
          }));
        }
      }
    }
  }, [currentProductData.produto_id, supplierProducts]);

  // ✅ NOVO: Atualizar validação em tempo real quando parâmetros do produto atual mudam
  useEffect(() => {
    if (currentProductData.produto_id && supplierProducts.length > 0) {
      const produtoCompleto = supplierProducts.find(p => p.id === currentProductData.produto_id);
      const parametros = produtoCompleto?.parametros_qualidade || produtoCompleto?.parametros || [];
      
      if (parametros.length > 0) {
        const results = {};
        parametros.forEach(param => {
          const valor = currentProductData.parametros[param.nome];
          if (valor !== undefined && valor !== null && valor !== '') {
            // ✅ Detectar se é parâmetro sensorial (IGUAL À VERSÃO ANTIGA)
            const isSensorial = param.tipo_limite === 'descritivo' || 
                               param.padrao?.toLowerCase() === 'sensorial' ||
                               (!param.valor_minimo && !param.valor_maximo && 
                                !param.limite_minimo && !param.limite_maximo &&
                                param.tipo_limite === 'descritivo');
            
            if (isSensorial && typeof valor === 'object') {
              // Para sensoriais, validar se está conforme ou tem descrição
              if (valor.conforme === true) {
                results[param.nome] = { status: 'success', message: 'Conforme com o esperado' };
              } else if (valor.descricao && valor.descricao.trim()) {
                results[param.nome] = { status: 'warning', message: 'Não conforme - descrição fornecida' };
              } else {
                results[param.nome] = { status: 'error', message: 'Não conforme - descrição obrigatória' };
              }
            } else if (!isSensorial) {
              results[param.nome] = validateParameter(param, valor);
            }
          }
        });
        setProductValidationResults(results);
        
        // Calcular conformidade
        const totalParams = parametros.length;
        let conformeParams = 0;

        parametros.forEach(param => {
          const valor = currentProductData.parametros[param.nome];
          if (valor !== undefined && valor !== null && valor !== '') {
            // ✅ Detectar se é parâmetro sensorial (IGUAL À VERSÃO ANTIGA)
            const isSensorial = param.tipo_limite === 'descritivo' || 
                               param.padrao?.toLowerCase() === 'sensorial' ||
                               (!param.valor_minimo && !param.valor_maximo && 
                                !param.limite_minimo && !param.limite_maximo &&
                                param.tipo_limite === 'descritivo');
            
            if (isSensorial && typeof valor === 'object') {
              if (valor.conforme === true) {
                conformeParams++;
              }
            } else if (!isSensorial) {
              const validation = validateParameter(param, valor);
              if (validation.status === 'success') {
                conformeParams++;
              }
            }
          }
        });

        const score = totalParams > 0 ? Math.round((conformeParams / totalParams) * 100) : 0;
        setProductConformityScore(score);
      } else {
        // Sem parâmetros
        setProductValidationResults({});
        setProductConformityScore(100);
      }
    }
  }, [currentProductData.parametros, currentProductData.produto_id, supplierProducts]);

  // Estados para captura de mídia
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'photo' ou 'video'
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [cameraZoom, setCameraZoom] = useState(1); // Zoom da câmera (1 = sem zoom)
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [cameraContext, setCameraContext] = useState('geral'); // 'geral' | 'especifico'
  const [cameraCategoria, setCameraCategoria] = useState('outras'); // categoria ao tirar foto geral
  
  // Estados para visualização de mídia (galeria com navegação)
  const [viewingMedia, setViewingMedia] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaGallery, setMediaGallery] = useState([]);
  const [mediaGalleryIndex, setMediaGalleryIndex] = useState(0);

  // Iniciar câmera
  const startCamera = async (type) => {
    try {
      setMediaType(type);
      setIsCameraActive(true);
      setCameraZoom(1); // Reset zoom
      
      // ✅ CORREÇÃO: Não incluir zoom nas constraints obrigatórias
      // Algumas câmeras não suportam zoom constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Câmera traseira
          width: { ideal: 1920 },
          height: { ideal: 1080 }
          // ✅ Removido: zoom constraints (será aplicado depois se suportado)
        },
        audio: type === 'video' // Áudio apenas para vídeo
      });
      
      setCameraStream(stream);
      
      // ✅ Verificar e aplicar zoom apenas se a câmera suportar
      if (stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          // Câmera suporta zoom nativo
          try {
            await track.applyConstraints({ advanced: [{ zoom: 1 }] });
          } catch (error) {
            console.log('Erro ao aplicar zoom inicial:', error);
          }
        }
        // Se não suportar zoom nativo, usaremos CSS transform (já configurado)
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      alert('Erro ao acessar câmera. Use a opção de selecionar arquivo.');
    }
  };
  
  // Ajustar zoom da câmera
  const adjustCameraZoom = async (delta) => {
    if (!cameraStream) return;
    
    const track = cameraStream.getVideoTracks()[0];
    if (!track) return;
    
    try {
      const capabilities = track.getCapabilities();
      if (capabilities && capabilities.zoom) {
        // Câmera suporta zoom nativo
        const settings = track.getSettings();
        const currentZoom = settings.zoom || 1;
        const newZoom = Math.max(
          capabilities.zoom.min || 1,
          Math.min(capabilities.zoom.max || 5, currentZoom + delta)
        );
        
        try {
          await track.applyConstraints({ advanced: [{ zoom: newZoom }] });
          setCameraZoom(newZoom);
        } catch (error) {
          console.log('Erro ao aplicar zoom nativo, usando CSS transform:', error);
          // Fallback para CSS transform
          const newZoom = Math.max(1, Math.min(5, cameraZoom + delta));
          setCameraZoom(newZoom);
        }
      } else {
        // Câmera não suporta zoom nativo, usar CSS transform
        const newZoom = Math.max(1, Math.min(5, cameraZoom + delta));
        setCameraZoom(newZoom);
      }
    } catch (error) {
      console.log('Erro ao verificar capabilities, usando CSS transform:', error);
      // Fallback para CSS transform
      const newZoom = Math.max(1, Math.min(5, cameraZoom + delta));
      setCameraZoom(newZoom);
    }
  };

  // Parar câmera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setCapturedMedia(null);
    setMediaType(null);
    setIsRecording(false);
    setMediaRecorder(null);
  };

  // Capturar foto
  const capturePhoto = () => {
    if (!cameraStream) return;
    
    const video = document.createElement('video');
    video.srcObject = cameraStream;
    video.play();
    
    video.onloadedmetadata = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedMedia(file);
      }, 'image/jpeg', 0.9);
    };
  };

  // Iniciar gravação de vídeo
  const startVideoRecording = () => {
    if (!cameraStream) return;
    
    const recorder = new MediaRecorder(cameraStream, {
      mimeType: 'video/webm;codecs=vp9'
    });
    
    const chunks = [];
    recorder.ondataavailable = (e) => chunks.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
      setCapturedMedia(file);
    };
    
    setMediaRecorder(recorder);
    recorder.start();
    setIsRecording(true);
  };

  // Parar gravação de vídeo
  const stopVideoRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // ✅ MODIFICADO: Salvar mídia capturada no contexto correto (geral ou específico)
  const saveCapturedMedia = () => {
    if (capturedMedia) {
      const type = mediaType === 'photo' ? 'fotos' : 'videos';
      
      if (cameraContext === 'geral') {
        handleFileUploadForBasicData([capturedMedia], type, cameraCategoria || 'outras');
      } else {
        // Salvar em currentProductData (fotos/vídeos específicos)
        handleFileUploadForProduct([capturedMedia], type);
      }
      stopCamera();
    }
  };

  // Remover mídia (foto ou vídeo)
  const removeMedia = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const uploadMediaFile = async (file, type) => {
    const empresaId = getEmpresaId();
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.includes('.') ? file.name.split('.').pop() : file.type.split('/').pop() || 'bin';
    const fileName = `${type}/${empresaId}/${timestamp}_${randomId}.${fileExtension}`;

    const storageRef = ref(storage, `recebimentos/${fileName}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    let thumbnailUrl = downloadURL;

    if (type === 'videos' && file.type.startsWith('video/')) {
      try {
        const video = document.createElement('video');
        video.src = URL.createObjectURL(file);
        video.currentTime = 1;

        await new Promise((resolve, reject) => {
          video.onloadeddata = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(video, 0, 0);
              canvas.toBlob(async (blob) => {
                if (blob) {
                  const thumbnailName = fileName.replace(/\.[^/.]+$/, '_thumb.jpg');
                  const thumbnailRef = ref(storage, `recebimentos/${thumbnailName}`);
                  await uploadBytes(thumbnailRef, blob);
                  thumbnailUrl = await getDownloadURL(thumbnailRef);
                }
                resolve();
              }, 'image/jpeg', 0.8);
            } catch (error) {
              console.log('Erro ao gerar thumbnail do vídeo:', error);
              resolve();
            }
          };
          video.onerror = reject;
        });
      } catch (error) {
        console.log('Erro ao gerar thumbnail do vídeo:', error);
      }
    }

    return {
      id: `${type}-${timestamp}-${randomId}`,
      name: file.name,
      size: file.size,
      type: file.type,
      url: downloadURL,
      thumbnailUrl,
      storagePath: `recebimentos/${fileName}`,
      uploadedAt: new Date().toISOString(),
      isOffline: false
    };
  };

  const createOfflineMediaEntry = async (file, type) => {
    const dataUrl = await fileToDataUrl(file);
    return {
      id: `${type}-offline-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      dataUrl,
      thumbnailUrl: type === 'fotos' ? dataUrl : null,
      isOffline: true,
      createdAt: new Date().toISOString()
    };
  };

  const saveFilesOffline = async (files, type) => {
    const offlineEntries = [];
    for (const file of files) {
      offlineEntries.push(await createOfflineMediaEntry(file, type));
    }
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], ...offlineEntries]
    }));
  };

  // ✅ NOVA FUNÇÃO: Upload para dados básicos (gerais) — fotos por categoria; vídeos em videos_gerais
  const handleFileUploadForBasicData = async (files, type, categoria = 'outras') => {
    if (!files || files.length === 0) return;

    const catId = FOTOS_RECEBIMENTO_CATEGORIAS.some((c) => c.id === categoria) ? categoria : 'outras';

    if (type === 'videos') {
      if (!isOnline) {
        const offlineEntries = [];
        for (const file of files) {
          offlineEntries.push(await createOfflineMediaEntry(file, type));
        }
        setBasicData((prev) => ({
          ...prev,
          videos_gerais: [...(prev.videos_gerais || []), ...offlineEntries]
        }));
        alert('Sem conexão. Arquivos salvos como rascunho neste dispositivo.');
        return;
      }
      setUploadingFiles(true);
      try {
        const uploadedFiles = [];
        for (const file of files) {
          uploadedFiles.push(await uploadMediaFile(file, type));
        }
        setBasicData((prev) => ({
          ...prev,
          videos_gerais: [...(prev.videos_gerais || []), ...uploadedFiles]
        }));
      } catch (error) {
        console.error('Erro ao fazer upload dos arquivos:', error);
        alert('Erro ao fazer upload dos arquivos: ' + error.message);
      } finally {
        setUploadingFiles(false);
      }
      return;
    }

    // fotos → fotos_por_categoria[catId]
    if (!isOnline) {
      const offlineEntries = [];
      for (const file of files) {
        const entry = await createOfflineMediaEntry(file, type);
        offlineEntries.push({ ...entry, categoria: catId });
      }
      setBasicData((prev) => {
        const cats = normalizeFotosPorCategoria(prev.fotos_por_categoria, prev.fotos_gerais);
        return {
          ...prev,
          fotos_por_categoria: {
            ...cats,
            [catId]: [...(cats[catId] || []), ...offlineEntries]
          }
        };
      });
      alert('Sem conexão. Arquivos salvos como rascunho neste dispositivo.');
      return;
    }

    setUploadingFiles(true);
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const uploaded = await uploadMediaFile(file, type);
        uploadedFiles.push({ ...uploaded, categoria: catId });
      }
      setBasicData((prev) => {
        const cats = normalizeFotosPorCategoria(prev.fotos_por_categoria, prev.fotos_gerais);
        return {
          ...prev,
          fotos_por_categoria: {
            ...cats,
            [catId]: [...(cats[catId] || []), ...uploadedFiles]
          }
        };
      });
    } catch (error) {
      console.error('Erro ao fazer upload dos arquivos:', error);
      alert('Erro ao fazer upload dos arquivos: ' + error.message);
    } finally {
      setUploadingFiles(false);
    }
  };

  // ✅ NOVA FUNÇÃO: Upload para dados específicos da matéria-prima
  const handleFileUploadForProduct = async (files, type) => {
    if (!files || files.length === 0) return;

    const fieldName = type === 'fotos' ? 'fotos_especificas' : 'videos_especificas';

    if (!isOnline) {
      const offlineEntries = [];
      for (const file of files) {
        offlineEntries.push(await createOfflineMediaEntry(file, type));
      }
      setCurrentProductData(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), ...offlineEntries]
      }));
      alert('Sem conexão. Arquivos salvos como rascunho neste dispositivo.');
      return;
    }

    setUploadingFiles(true);
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const uploaded = await uploadMediaFile(file, type);
        uploadedFiles.push(uploaded);
      }
      
      setCurrentProductData(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Erro ao fazer upload dos arquivos:', error);
      alert('Erro ao fazer upload dos arquivos: ' + error.message);
    } finally {
      setUploadingFiles(false);
    }
  };

  // ✅ MANTIDO: Função original para compatibilidade
  // Manipular upload de arquivos com suporte offline
  const handleFileUpload = async (files, type) => {
    if (!files || files.length === 0) return;

    if (!isOnline) {
      await saveFilesOffline(files, type);
      alert('Sem conexão. Arquivos salvos como rascunho neste dispositivo.');
      return;
    }

    setUploadingFiles(true);
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const data = await uploadMediaFile(file, type);
        uploadedFiles.push(data);
      }

      setFormData(prev => ({
        ...prev,
        [type]: [...prev[type], ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Erro ao fazer upload dos arquivos:', error);
      if (!navigator.onLine || /network|offline/i.test(error.message || '')) {
        await saveFilesOffline(files, type);
        alert('Conexão instável. Arquivos salvos como rascunho neste dispositivo.');
      } else {
        alert('Erro ao fazer upload dos arquivos. Tente novamente.');
      }
    } finally {
      setUploadingFiles(false);
    }
  };
  
  // Abrir modal de visualização de mídia (com galeria opcional para setas)
  const normalizeMediaItem = (media, type) => {
    let normalizedMedia;
    if (typeof media === 'string') {
      normalizedMedia = { url: media };
    } else if (media && typeof media === 'object') {
      normalizedMedia = { ...media };
      if (!normalizedMedia.url && !normalizedMedia.dataUrl && !normalizedMedia.thumbnailUrl) {
        normalizedMedia.url = String(media);
      }
    } else {
      normalizedMedia = { url: String(media || '') };
    }
    return { ...normalizedMedia, type };
  };

  const openMediaViewer = (media, type, gallery = null, index = 0) => {
    const list = Array.isArray(gallery) && gallery.length > 0
      ? gallery.map((item) => normalizeMediaItem(item, type))
      : [normalizeMediaItem(media, type)];
    const safeIndex = Math.max(0, Math.min(index, list.length - 1));
    setMediaGallery(list);
    setMediaGalleryIndex(safeIndex);
    setViewingMedia(list[safeIndex]);
    setShowMediaModal(true);
  };

  const navigateMediaGallery = useCallback((delta) => {
    setMediaGallery((prevGallery) => {
      if (!prevGallery || prevGallery.length <= 1) return prevGallery;
      setMediaGalleryIndex((prevIndex) => {
        const next = (prevIndex + delta + prevGallery.length) % prevGallery.length;
        setViewingMedia(prevGallery[next]);
        return next;
      });
      return prevGallery;
    });
  }, []);

  useEffect(() => {
    if (!showMediaModal || mediaGallery.length <= 1) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateMediaGallery(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateMediaGallery(1);
      } else if (e.key === 'Escape') {
        setShowMediaModal(false);
        setViewingMedia(null);
        setMediaGallery([]);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showMediaModal, mediaGallery.length, navigateMediaGallery]);

  const ensureOfflineMediaUploaded = async () => {
    const hasOfflineFotos = formData.fotos.some((media) => media?.isOffline && media?.dataUrl);
    const hasOfflineVideos = formData.videos.some((media) => media?.isOffline && media?.dataUrl);

    if (!hasOfflineFotos && !hasOfflineVideos) {
      return { fotos: formData.fotos, videos: formData.videos };
    }

    if (!isOnline) {
      throw new Error('offline_media_pending');
    }

    setUploadingFiles(true);
    try {
      const processMedia = async (items, type) => {
        const processed = [];
        for (const media of items) {
          if (media?.isOffline && media?.dataUrl) {
            const file = dataUrlToFile(media.dataUrl, media.name || `${type}-${Date.now()}`, media.type || (type === 'fotos' ? 'image/jpeg' : 'video/webm'));
            const uploaded = await uploadMediaFile(file, type);
            processed.push(uploaded);
          } else {
            processed.push(media);
          }
        }
        return processed;
      };

      const finalFotos = await processMedia(formData.fotos, 'fotos');
      const finalVideos = await processMedia(formData.videos, 'videos');

      setFormData((prev) => ({
        ...prev,
        fotos: finalFotos,
        videos: finalVideos
      }));

      return { fotos: finalFotos, videos: finalVideos };
    } finally {
      setUploadingFiles(false);
    }
  };

  const resetRecebimentoEmAndamento = () => {
    setCurrentStep(1);
    setSelectedSupplier(null);
    setBasicData(createInitialBasicData());
    setSupplierProducts([]);
    setProcessedProducts({});
    setCurrentProductData(createInitialProductData());
    setShowProductModal(false);
    setSelectedProduct(null);
    setFormData(createInitialFormData());
    setActiveDraftId(null);
  };

  const handleCancelRecebimento = () => {
    const temDados =
      !!selectedSupplier ||
      !!selectedProduct ||
      !!basicData.numeroNotaFiscal ||
      Object.keys(processedProducts || {}).length > 0 ||
      currentStep > 1;
    if (
      temDados &&
      !window.confirm('Cancelar este recebimento? Os dados não salvos como rascunho serão perdidos.')
    ) {
      return;
    }
    resetRecebimentoEmAndamento();
    setActiveTab('novo');
  };

  const handleSaveDraft = async ({ silent = false, pendingDecision = null } = {}) => {
    const fluxoNovo = !!selectedSupplier;
    if (!fluxoNovo && !selectedProduct) {
      if (!silent) {
        alert('Selecione um fornecedor (ou matéria-prima) antes de salvar o rascunho.');
      }
      return;
    }

    try {
      if (!silent) setSavingDraft(true);
      const payload = {
        id: activeDraftId || undefined,
        flowVersion: fluxoNovo ? 2 : 1,
        formData: deepClone(formData),
        basicData: deepClone(basicData),
        currentProductData: deepClone(currentProductData),
        processedProducts: deepClone(processedProducts),
        selectedSupplierSnapshot: deepClone(selectedSupplier),
        selectedSupplierId: selectedSupplier?.id || null,
        selectedSupplierNome: selectedSupplier?.nome_empresa || null,
        supplierProductsSnapshot: deepClone(supplierProducts),
        selectedProductSnapshot: deepClone(selectedProduct),
        selectedProductId: selectedProduct?.id,
        selectedProductNome:
          selectedProduct?.nome ||
          selectedProduct?.nome_produto ||
          selectedSupplier?.nome_empresa ||
          'Recebimento',
        currentStep,
        empresaId: companyId || getEmpresaId(),
        userId: user?.uid,
        userName: user?.displayName || userData?.nome || user?.email || 'Usuário',
        pendingDecision,
        savedAt: new Date().toISOString()
      };

      const draftId = await saveReceivingDraft(payload);
      setActiveDraftId(draftId);
      await refreshDrafts();

      if (!silent) {
        alert('Rascunho salvo neste dispositivo.');
      }
    } catch (error) {
      console.error('Erro ao salvar rascunho offline:', error);
      if (!silent) {
        alert('Não foi possível salvar o rascunho.');
      }
    } finally {
      if (!silent) setSavingDraft(false);
    }
  };

  const handleLoadDraft = async (draft) => {
    if (!draft) return;
    setActiveTab('novo');
    setActiveDraftId(draft.id);

    if (draft.flowVersion === 2 || draft.selectedSupplierSnapshot) {
      setSelectedSupplier(draft.selectedSupplierSnapshot || null);
      setBasicData({
        ...createInitialBasicData(),
        ...(draft.basicData || {}),
        fotos_por_categoria: normalizeFotosPorCategoria(
          draft.basicData?.fotos_por_categoria,
          draft.basicData?.fotos_gerais
        )
      });
      setProcessedProducts(draft.processedProducts || {});
      setCurrentProductData({
        ...createInitialProductData(),
        ...(draft.currentProductData || {})
      });
      setFormData(createInitialFormData());
      setSelectedProduct(null);

      const produtosSalvos = draft.supplierProductsSnapshot;
      if (Array.isArray(produtosSalvos) && produtosSalvos.length > 0) {
        setSupplierProducts(produtosSalvos);
      } else if (draft.selectedSupplierId || draft.selectedSupplierSnapshot?.id) {
        await loadSupplierProducts(draft.selectedSupplierId || draft.selectedSupplierSnapshot.id);
      } else {
        setSupplierProducts([]);
      }

      setCurrentStep(draft.currentStep || 2);
      alert('Rascunho carregado. Continue o recebimento ou finalize quando estiver pronto.');
      return;
    }

    const draftFormData = {
      ...createInitialFormData(),
      ...draft.formData,
      fotos: draft.formData?.fotos || [],
      videos: draft.formData?.videos || [],
      parametros: draft.formData?.parametros || {}
    };
    setFormData(draftFormData);
    setSelectedProduct(draft.selectedProductSnapshot || null);
    setSelectedSupplier(null);
    setBasicData(createInitialBasicData());
    setSupplierProducts([]);
    setProcessedProducts({});
    setCurrentStep(draft.currentStep || 1);
    alert('Rascunho carregado. Revise e finalize quando estiver conectado.');
  };

  const handleDeleteDraft = async (draftId) => {
    if (!draftId) return;
    if (!window.confirm('Remover este rascunho deste dispositivo?')) return;
    try {
      await deleteReceivingDraft(draftId);
      if (activeDraftId === draftId) {
        setActiveDraftId(null);
      }
      await refreshDrafts();
    } catch (error) {
      console.error('Erro ao remover rascunho:', error);
      alert('Não foi possível remover o rascunho.');
    }
  };
  
  // ✅ NOVO: Processar lotes do fornecedor (suporta único, múltiplos e ranges)
  const processarLotes = (input) => {
    if (!input || !input.trim()) return [];
    
    const lotes = [];
    const inputLimpo = input.trim();
    
    // Dividir por vírgulas para processar múltiplos lotes/ranges
    const partes = inputLimpo.split(',').map(p => p.trim()).filter(p => p);
    
    for (const parte of partes) {
      // Verificar se é um range (formato: "de X a Y" ou "X-Y" ou "X até Y")
      const rangePatterns = [
        /^de\s+(\d+)\s+a\s+(\d+)$/i,           // "de 03001 a 03020"
        /^de\s+(\d+)\s+até\s+(\d+)$/i,         // "de 03001 até 03020"
        /^(\d+)\s+a\s+(\d+)$/i,                // "03001 a 03020"
        /^(\d+)\s+até\s+(\d+)$/i,              // "03001 até 03020"
        /^(\d+)\s*-\s*(\d+)$/i,                // "03001-03020" ou "03001 - 03020"
        /^(\d+)\s*\.\.\s*(\d+)$/i              // "03001..03020"
      ];
      
      let rangeEncontrado = false;
      for (const pattern of rangePatterns) {
        const match = parte.match(pattern);
        if (match) {
          const inicio = parseInt(match[1], 10);
          const fim = parseInt(match[2], 10);
          
          if (inicio <= fim) {
            // Expandir range para lotes individuais
            for (let i = inicio; i <= fim; i++) {
              // Manter o padding original se houver zeros à esquerda
              const padding = match[1].length;
              const loteFormatado = String(i).padStart(padding, '0');
              lotes.push(loteFormatado);
            }
            rangeEncontrado = true;
            break;
          }
        }
      }
      
      // Se não for range, adicionar como lote único
      if (!rangeEncontrado) {
        lotes.push(parte);
      }
    }
    
    // Remover duplicatas e ordenar
    return [...new Set(lotes)].sort();
  };
  
  // ✅ NOVO: Formatar lotes para exibição
  const formatarLotesParaExibicao = (lotes) => {
    if (!lotes || lotes.length === 0) return 'N/A';
    if (Array.isArray(lotes)) {
      if (lotes.length === 1) return lotes[0];
      if (lotes.length <= 5) return lotes.join(', ');
      return `${lotes.slice(0, 3).join(', ')}... (+${lotes.length - 3} lotes)`;
    }
    // Compatibilidade com dados antigos (string)
    return String(lotes);
  };

  const montarLotesDetalhe = (loteInput, lotesQuantidades = {}, quantidadeUnica = '') => {
    const lotes = processarLotes(loteInput);
    if (lotes.length === 0) {
      return { lotes: [], detalhes: null, total: null, erros: ['Informe ao menos um lote'] };
    }

    if (lotes.length === 1) {
      const qtd = parseFloat(
        formatarNumeroParaCalculo(lotesQuantidades?.[lotes[0]] || quantidadeUnica || '')
      );
      if (!Number.isFinite(qtd) || qtd <= 0) {
        return { lotes, detalhes: null, total: null, erros: ['Informe a quantidade recebida'] };
      }
      return {
        lotes,
        detalhes: [{ lote: lotes[0], quantidade: qtd }],
        total: qtd,
        erros: []
      };
    }

    const erros = [];
    const detalhes = lotes.map((lote) => {
      const qtd = parseFloat(formatarNumeroParaCalculo(lotesQuantidades?.[lote] || ''));
      if (!Number.isFinite(qtd) || qtd <= 0) {
        erros.push(`Informe a quantidade do lote ${lote}`);
      }
      return { lote, quantidade: Number.isFinite(qtd) ? qtd : 0 };
    });
    const total = detalhes.reduce((s, d) => s + d.quantidade, 0);
    return { lotes, detalhes, total, erros };
  };

  const syncLotesQuantidades = (loteInput, prevMap = {}, quantidadeFallback = '') => {
    const lotes = processarLotes(loteInput);
    const next = {};
    lotes.forEach((lote) => {
      if (prevMap[lote] != null && prevMap[lote] !== '') {
        next[lote] = prevMap[lote];
      } else if (lotes.length === 1 && quantidadeFallback) {
        next[lote] = quantidadeFallback;
      } else {
        next[lote] = prevMap[lote] || '';
      }
    });
    return next;
  };

  // Finalizar recebimento
  // ✅ NOVA FUNÇÃO: Salvar recebimento individual de uma matéria-prima
  const handleSaveProductReceiving = async (decision) => {
    if (!selectedSupplier || !currentProductData.produto_id) {
      alert('Erro: Fornecedor ou matéria-prima não selecionados.');
      return;
    }

    if (!isOnline) {
      alert('Sem conexão. Por favor, conecte-se à internet para salvar o recebimento.');
      return;
    }

    const produtoJaProcessado = processedProducts[currentProductData.produto_id];
    if (produtoJaProcessado?.count > 0) {
      const ok = window.confirm(
        `Este produto já foi processado ${produtoJaProcessado.count} vez(es) nesta nota fiscal.\n\n` +
          'Se for a mesma NF, fornecedor e lote(s), o sistema bloqueará a duplicata.\n\nDeseja continuar mesmo assim?'
      );
      if (!ok) return;
    }

    try {
      setLoading(true);
      const empresaAtual = companyId || getEmpresaId();
      if (!empresaAtual) {
        throw new Error('ID da empresa não encontrado');
      }

      // Fotos por categoria (obrigatórias) + fotos específicas do produto (adicionais)
      const faltandoFotos = validarFotosObrigatoriasRecebimento(basicData.fotos_por_categoria);
      if (faltandoFotos.length > 0) {
        alert(
          `Fotos obrigatórias pendentes:\n• ${faltandoFotos.join('\n• ')}\n\nEnvie ao menos uma foto em cada categoria antes de salvar.`
        );
        setLoading(false);
        return;
      }

      const fotosCombinadas = [
        ...flattenFotosPorCategoria(basicData.fotos_por_categoria),
        ...(currentProductData.fotos_especificas || []).map((f) =>
          typeof f === 'object' && f !== null
            ? { ...f, categoria: f.categoria || 'outras' }
            : { url: String(f), categoria: 'outras' }
        )
      ];
      const videosCombinados = [...(basicData.videos_gerais || []), ...(currentProductData.videos_especificas || [])];

      // Buscar produto completo para obter parâmetros
      const produtoCompleto = supplierProducts.find(p => p.id === currentProductData.produto_id);

      const { lotes, detalhes, total, erros } = montarLotesDetalhe(
        currentProductData.lote_fornecedor,
        currentProductData.lotesQuantidades,
        currentProductData.quantidade
      );
      if (erros.length > 0) {
        alert(erros[0]);
        setLoading(false);
        return;
      }
      
      const recebimentoData = {
        empresa_id: empresaAtual,
        produto_id: currentProductData.produto_id,
        produto_nome: currentProductData.produto_nome,
        produto_codigo: produtoCompleto?.codigo || produtoCompleto?.codigo_produto || produtoCompleto?.codigo_origem || '',
        fornecedor: basicData.fornecedor,
        fornecedor_id: basicData.fornecedor_id,
        lote_fornecedor: lotes,
        lotes_detalhe: detalhes,
        quantidade_recebida: total,
        unidade_medida: currentProductData.unidade,
        data_recebimento: basicData.dataRecebimento || toYmdLocal(new Date()),
        data_fabricacao: currentProductData.dataFabricacao ? converterDataParaISO(currentProductData.dataFabricacao) : null,
        data_validade: currentProductData.dataValidade ? converterDataParaISO(currentProductData.dataValidade) : null,
        observacoes_recebedor: currentProductData.observacoes || '',
        // Dados básicos (replicados)
        numero_nota_fiscal: basicData.numeroNotaFiscal || null,
        numero_dcpoa: basicData.dcpoaNaoSeAplica ? null : (basicData.numeroDcpoa || null),
        dcpoa_nao_se_aplica: basicData.dcpoaNaoSeAplica,
        numero_lacre: basicData.lacreNaoSeAplica ? null : (basicData.numeroLacre || null),
        lacre_nao_se_aplica: basicData.lacreNaoSeAplica,
        placa_caminhao: basicData.placaCaminhao || null,
        condicoes_transporte: basicData.condicoesTransporte || null,
        temperatura_carga: basicData.temperaturaCarga ? parseFloat(formatarNumeroParaCalculo(basicData.temperaturaCarga)) : null,
        parametros_coletados: currentProductData.parametros || {},
        validationResults: productValidationResults,
        conformityScore: productConformityScore,
        recommendation: productRecommendation?.action || 'ANALISAR',
        decision: decision || 'ANALISAR',
        fotos: fotosCombinadas,
        videos: videosCombinados,
        recebidoPor: {
          uid: user.uid,
          nome: user.displayName || userData?.nome || userData?.displayName || user.email || 'Usuário',
          email: user.email || userData?.email || ''
        },
        decisao_final: decision === 'ACEITAR' ? 'aceitar' : decision === 'REJEITAR' ? 'rejeitar' : 'em_analise_qualidade',
        criado_em: new Date(),
        atualizado_em: new Date()
      };

      const createReceiving = httpsCallable(functions, 'createReceiving');
      const result = await createReceiving(recebimentoData);

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao salvar recebimento');
      }

      const recId = result.data?.data?.id;
      if (decision === 'ACEITAR' && recId && moduloArmazenamento) {
        abrirPalletsRecebimento({
          id: recId,
          produto_nome: currentProductData.produto_nome,
          quantidade_recebida: total,
          unidade_medida: currentProductData.unidade,
          decisao_final: 'aceitar'
        });
      }

      // Recarregar lista de recebimentos
      await loadRecebimentos();

      // Notificar outros componentes
      window.dispatchEvent(new CustomEvent('recebimentoAtualizado'));

    } catch (error) {
      console.error('Erro ao salvar recebimento:', error);
      const msg =
        error?.details ||
        error?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, '') ||
        'Erro ao salvar recebimento.';
      alert(msg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleFinishReceiving = async (decision) => {
    if (!selectedProduct) {
      alert('Selecione uma matéria-prima antes de finalizar o recebimento.');
      return;
    }

    if (!isOnline) {
      await handleSaveDraft({ silent: true, pendingDecision: decision });
      alert('Sem conexão. O recebimento foi salvo como rascunho neste dispositivo.');
      return;
    }

    try {
      setLoading(true);
      const empresaAtual = companyId || getEmpresaId();
      if (!empresaAtual) {
        throw new Error('ID da empresa não encontrado');
      }

      const { fotos: fotosProcessadas, videos: videosProcessados } = await ensureOfflineMediaUploaded();

      const { lotes, detalhes, total, erros } = montarLotesDetalhe(
        formData.lote_fornecedor,
        formData.lotesQuantidades,
        formData.quantidade
      );
      if (erros.length > 0) {
        alert(erros[0]);
        setLoading(false);
        return;
      }

      const recebimentoData = {
        empresa_id: empresaAtual,
        produto_id: selectedProduct.id, // ✅ CORREÇÃO: produtoId → produto_id
        produto_nome: selectedProduct.nome, // ✅ CORREÇÃO: Adicionar campos separados
        produto_codigo: selectedProduct.codigo, // ✅ CORREÇÃO: Adicionar campos separados
        fornecedor: formData.fornecedor,
        lote_fornecedor: lotes,
        lotes_detalhe: detalhes,
        quantidade_recebida: total,
        unidade_medida: formData.unidade, // ✅ CORREÇÃO: unidade → unidade_medida
        data_recebimento: formData.dataRecebimento || basicData.dataRecebimento || toYmdLocal(new Date()),
        data_fabricacao: formData.dataFabricacao ? converterDataParaISO(formData.dataFabricacao) : null,
        data_validade: formData.dataValidade ? converterDataParaISO(formData.dataValidade) : null,
        observacoes_recebedor: formData.observacoes, // ✅ CORREÇÃO: observacoes → observacoes_recebedor
        numero_nota_fiscal: formData.numeroNotaFiscal || null,
        numero_dcpoa: formData.dcpoaNaoSeAplica ? null : (formData.numeroDcpoa || null),
        dcpoa_nao_se_aplica: formData.dcpoaNaoSeAplica,
        numero_lacre: formData.lacreNaoSeAplica ? null : (formData.numeroLacre || null),
        lacre_nao_se_aplica: formData.lacreNaoSeAplica,
        placa_caminhao: formData.placaCaminhao || null,
        condicoes_transporte: formData.condicoesTransporte || null,
        temperatura_carga: formData.temperaturaCarga ? parseFloat(formatarNumeroParaCalculo(formData.temperaturaCarga)) : null, // ✅ CORREÇÃO: Converter vírgula para ponto
        parametros_coletados: (() => {
          // ✅ CORREÇÃO: Converter vírgulas em pontos nos valores numéricos dos parâmetros
          const parametrosProcessados = {};
          Object.keys(formData.parametros).forEach(key => {
            const valor = formData.parametros[key];
            if (typeof valor === 'string' && valor.includes(',')) {
              parametrosProcessados[key] = formatarNumeroParaCalculo(valor);
            } else {
              parametrosProcessados[key] = valor;
            }
          });
          return parametrosProcessados;
        })(), // ✅ CORREÇÃO: parametros → parametros_coletados
        validationResults,
        conformityScore,
        recommendation: recommendation.action,
        decision,
        fotos: fotosProcessadas,
        videos: videosProcessados,
        recebidoPor: {
          uid: user.uid,
          nome: user.displayName || userData?.nome || userData?.displayName || user.email || 'Usuário',
          email: user.email || userData?.email || ''
        },
        decisao_final: decision === 'ACEITAR' ? 'aceitar' : decision === 'REJEITAR' ? 'rejeitar' : 'em_analise_qualidade',
        criado_em: new Date(),
        atualizado_em: new Date()
      };

      // ✅ CORREÇÃO: Usar httpsCallable como na página de produtos
      const createReceiving = httpsCallable(functions, 'createReceiving');
      
      const result = await createReceiving(recebimentoData);

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao salvar recebimento');
      }

      const recId = result.data?.data?.id;
      if (decision === 'ACEITAR' && recId && moduloArmazenamento) {
        abrirPalletsRecebimento({
          id: recId,
          produto_nome: selectedProduct.nome,
          quantidade_recebida: total,
          unidade_medida: formData.unidade,
          decisao_final: 'aceitar'
        });
      }

      // Reset do formulário
      setCurrentStep(1);
      setSelectedProduct(null);
      setFormData(createInitialFormData());
      setValidationResults({});
      setConformityScore(0);
      setRecommendation('');

      if (activeDraftId) {
        await deleteReceivingDraft(activeDraftId);
        setActiveDraftId(null);
        await refreshDrafts();
      }

      // Recarregar lista de recebimentos
      await loadRecebimentos();

      // ✅ NOVO: Notificar outros componentes sobre a atualização
      console.log('📢 Disparando evento recebimentoAtualizado...');
      window.dispatchEvent(new CustomEvent('recebimentoAtualizado'));

      alert('Recebimento criado com sucesso!');
    } catch (error) {
      console.error('Erro ao finalizar recebimento:', error);
      if (!navigator.onLine) {
        await handleSaveDraft({ silent: true, pendingDecision: decision });
        alert('Conexão perdida. Recebimento salvo como rascunho para envio posterior.');
      } else {
        const msg =
          error?.details ||
          error?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, '') ||
          'Erro ao finalizar recebimento';
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const UNIDADES_QUANTIDADE = ['Kg', 'g', 'Ton', 'L', 'mL', 'Unid.'];

  const normalizarUnidadeMedida = (unidade) => {
    const raw = String(unidade || '').trim();
    if (!raw) return 'Kg';
    if (UNIDADES_QUANTIDADE.includes(raw)) return raw;
    const key = raw.toLowerCase().replace(/\s+/g, '');
    const map = {
      kg: 'Kg',
      kilograma: 'Kg',
      kilogramas: 'Kg',
      g: 'g',
      grama: 'g',
      gramas: 'g',
      ton: 'Ton',
      tonelada: 'Ton',
      toneladas: 'Ton',
      l: 'L',
      litro: 'L',
      litros: 'L',
      ml: 'mL',
      mililitro: 'mL',
      mililitros: 'mL',
      'unid.': 'Unid.',
      unid: 'Unid.',
      unidade: 'Unid.',
      unidades: 'Unid.'
    };
    return map[key] || 'Kg';
  };

  const podeEditarQuantidadeUnidade = () =>
    isRole('superadmin') ||
    isRole('admin') ||
    isRole('administrador') ||
    isRole('gerente') ||
    isRole('recebedor') ||
    isRole('qualidade') ||
    hasPermission('manage_receivings') ||
    hasPermission('manage_stock');

  const podeExcluirRecebimento = () =>
    isRole('superadmin') ||
    isRole('admin') ||
    isRole('administrador');

  const handleExcluirRecebimento = async (recebimento) => {
    if (!recebimento?.id || !podeExcluirRecebimento()) return;
    const nome = recebimento.produto_nome || 'este recebimento';
    const nf = recebimento.numero_nota_fiscal ? ` (NF ${recebimento.numero_nota_fiscal})` : '';
    if (
      !window.confirm(
        `Excluir definitivamente o recebimento de "${nome}"${nf}?\n\nEssa ação remove o item do histórico e do estoque. Não pode ser desfeita.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const deleteReceiving = httpsCallable(functions, 'deleteReceiving');
      const empresaIdEnvio =
        recebimento.companyId ||
        recebimento.empresa_id ||
        companyId ||
        getEmpresaId();

      const result = await deleteReceiving({
        receivingId: recebimento.id,
        empresa_id: empresaIdEnvio
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao excluir recebimento');
      }

      setRecebimentos((prev) => prev.filter((r) => r.id !== recebimento.id));
      if (viewingReceiving?.id === recebimento.id) {
        setShowViewModal(false);
        setViewingReceiving(null);
      }
      window.dispatchEvent(new CustomEvent('recebimentoAtualizado'));
      alert('Recebimento excluído com sucesso.');
      await loadRecebimentos(recebimentosPagination.page || 1);
    } catch (err) {
      console.error('Erro ao excluir recebimento:', err);
      const msg =
        err?.details ||
        err?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, '') ||
        'Erro ao excluir recebimento.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const inicializarEdicaoQuantidadeUnidade = (recebimento) => {
    const qtd = recebimento?.quantidade_recebida;
    setEditViewQuantidade(
      qtd != null && qtd !== ''
        ? Number(qtd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : ''
    );
    setEditViewUnidade(normalizarUnidadeMedida(recebimento?.unidade_medida));
  };

  const inicializarEdicaoDataRecebimento = (recebimento) => {
    const date = parseAnyDate(getDataRecebimentoRaw(recebimento));
    setEditViewDataRecebimento(date ? toYmdLocal(date) : toYmdLocal(new Date()));
  };

  // Função para visualizar recebimento
  const handleViewReceiving = (recebimento) => {
    // Se tem decisão da qualidade, abrir modal de decisão final
    if (recebimento.decisao_final === 'aprovado_qualidade' || recebimento.decisao_final === 'rejeitado_qualidade') {
      setReceivingForFinalDecision(recebimento);
      setFinalDecision('');
      setFinalObservations('');
      setShowFinalDecisionModal(true);
    } else {
      // Caso contrário, apenas visualizar
      setViewingReceiving(recebimento);
      inicializarEdicaoQuantidadeUnidade(recebimento);
      inicializarEdicaoDataRecebimento(recebimento);
      setShowViewModal(true);
    }
  };

  const handleSalvarQuantidadeUnidadeHistorico = async () => {
    if (!viewingReceiving?.id) return;
    const qty = parseFloat(formatarNumeroParaCalculo(editViewQuantidade));
    if (!Number.isFinite(qty) || qty < 0) {
      alert('Informe uma quantidade válida (número maior ou igual a zero).');
      return;
    }
    if (qty > QUANTIDADE_MAXIMA) {
      alert('A quantidade máxima permitida é 999.999,99.');
      return;
    }
    if (!editViewUnidade) {
      alert('Selecione a unidade de medida.');
      return;
    }

    const unidadeNormalizada = normalizarUnidadeMedida(editViewUnidade);

    try {
      setSavingViewQuantidade(true);
      setError(null);
      const updateReceivingQuantity = httpsCallable(functions, 'updateReceivingQuantity');
      const empresaIdEnvio =
        viewingReceiving.companyId ||
        viewingReceiving.empresa_id ||
        companyId ||
        getEmpresaId();

      await updateReceivingQuantity({
        receivingId: viewingReceiving.id,
        nova_quantidade: qty,
        nova_unidade: unidadeNormalizada,
        empresa_id: empresaIdEnvio
      });

      const atualizado = {
        ...viewingReceiving,
        quantidade_recebida: qty,
        unidade_medida: unidadeNormalizada
      };
      setViewingReceiving(atualizado);
      setRecebimentos((prev) =>
        prev.map((r) => (r.id === atualizado.id ? { ...r, ...atualizado } : r))
      );
      setEditViewQuantidade(
        qty.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      );
      setEditViewUnidade(unidadeNormalizada);
      alert('Quantidade e unidade atualizadas com sucesso.');
    } catch (err) {
      console.error('Erro ao atualizar quantidade/unidade:', err);
      const msg =
        err?.details ||
        err?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, '') ||
        'Erro ao atualizar quantidade e unidade.';
      alert(msg);
    } finally {
      setSavingViewQuantidade(false);
    }
  };

  const handleSalvarDataRecebimentoHistorico = async () => {
    if (!viewingReceiving?.id) return;
    if (!editViewDataRecebimento || !/^\d{4}-\d{2}-\d{2}$/.test(editViewDataRecebimento)) {
      alert('Informe uma data de recebimento válida.');
      return;
    }

    try {
      setSavingViewDataRecebimento(true);
      setError(null);
      const updateReceivingDate = httpsCallable(functions, 'updateReceivingDate');
      const empresaIdEnvio =
        viewingReceiving.companyId ||
        viewingReceiving.empresa_id ||
        companyId ||
        getEmpresaId();

      await updateReceivingDate({
        receivingId: viewingReceiving.id,
        data_recebimento: editViewDataRecebimento,
        empresa_id: empresaIdEnvio
      });

      const dataParsed = parseAnyDate(editViewDataRecebimento);
      const criado = parseAnyDate(viewingReceiving.criado_em) || new Date();
      const dayRecv = dataParsed
        ? new Date(dataParsed.getFullYear(), dataParsed.getMonth(), dataParsed.getDate()).getTime()
        : null;
      const dayCriado = new Date(criado.getFullYear(), criado.getMonth(), criado.getDate()).getTime();
      const atualizado = {
        ...viewingReceiving,
        data_recebimento: dataParsed || editViewDataRecebimento,
        data_retroativa: dayRecv != null ? dayRecv < dayCriado : false
      };
      setViewingReceiving(atualizado);
      setRecebimentos((prev) =>
        prev.map((r) => (r.id === atualizado.id ? { ...r, ...atualizado } : r))
      );
      alert('Data do recebimento atualizada com sucesso.');
    } catch (err) {
      console.error('Erro ao atualizar data do recebimento:', err);
      const msg =
        err?.details ||
        err?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, '') ||
        'Erro ao atualizar data do recebimento.';
      alert(msg);
    } finally {
      setSavingViewDataRecebimento(false);
    }
  };

  // Função para decisão final do recebedor
  const handleFinalDecision = async () => {
    if (!finalDecision) {
      alert('Selecione uma decisão');
      return;
    }

    // ✅ NOVO: Validar observação obrigatória para Rejeitar ou Analisar
    if ((finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade') && !finalObservations.trim()) {
      alert('Observação é obrigatória ao escolher "Rejeitar" ou "Analisar". Por favor, preencha o campo de observações.');
      return;
    }

    try {
      setLoading(true);
      
      const updateFinalDecision = httpsCallable(functions, 'updateReceivingFinalDecision');
      
      const result = await updateFinalDecision({
        recebimento_id: receivingForFinalDecision.id,
        empresa_id: companyId,
        decisao_final: finalDecision,
        observacoes_recebedor: finalObservations
      });

      if (result.data?.success) {
        alert('Decisão registrada com sucesso!');
        const recDecidido = receivingForFinalDecision;
        setShowFinalDecisionModal(false);
        setReceivingForFinalDecision(null);
        if (finalDecision === 'aceitar' && recDecidido) {
          abrirPalletsRecebimento({ ...recDecidido, decisao_final: 'aceitar' });
        }
        await loadRecebimentos();
        
        // ✅ NOVO: Disparar evento para atualizar dashboard do recebedor
        console.log('📢 Disparando evento recebimentoAtualizado após decisão final...');
        window.dispatchEvent(new CustomEvent('recebimentoAtualizado'));
      } else {
        throw new Error(result.data?.message || 'Erro ao registrar decisão');
      }
    } catch (error) {
      console.error('Erro ao registrar decisão final:', error);
      alert('Erro ao registrar decisão: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProdutos = produtos.filter((produto) =>
    insumoCorrespondeTermo(produto, searchTermProduto)
  );

  // Filtros de busca/status/data já aplicados no servidor (listReceivings)
  const filteredRecebimentos = recebimentos;



  if (loading && currentStep === 1) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Recebimentos</h1>
              <p className="text-gray-600 mt-1">Gestão de recebimentos de produtos</p>
            </div>
            

          </div>

          {/* Progress Steps */}
          {currentStep > 1 && (
            <div className="mt-6">
              <div className="flex items-center justify-between">
                {[
                  { step: 1, label: 'Produto', icon: Package },
                  { step: 2, label: 'Coleta', icon: ClipboardList },
                  { step: 3, label: 'Validação', icon: CheckCircle },
                  { step: 4, label: 'Decisão', icon: FileText }
                ].map(({ step, label, icon: Icon }) => (
                  <div key={step} className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`ml-2 text-sm font-medium ${
                      currentStep >= step ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {label}
                    </span>
                    {step < 4 && (
                      <div className={`w-16 h-1 mx-4 ${
                        currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 1: Seleção de Produto */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {!isOnline && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <WifiOff className="w-5 h-5 text-yellow-700 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-900">Sem conexão com a internet</p>
                  <p className="text-sm text-yellow-800">
                    Continue o cadastro normalmente e clique em &quot;Salvar rascunho offline&quot; para não perder as informações.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HardDrive className="w-4 h-4" />
                {draftsLoading ? 'Carregando rascunhos...' : `${drafts.length} rascunho(s) neste dispositivo`}
              </div>
              <button
                type="button"
                onClick={refreshDrafts}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </button>
            </div>

            {drafts.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">Rascunhos Pendentes</p>
                    <p className="text-sm text-gray-600">Armazenados neste dispositivo — clique em Retomar para continuar</p>
                  </div>
                </div>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {drafts.map((draft) => (
                    <div
                      key={draft.id}
                      className={`p-3 rounded-lg border ${activeDraftId === draft.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {draft.selectedSupplierNome ||
                              draft.basicData?.fornecedor ||
                              draft.selectedProductNome ||
                              'Recebimento'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {draft.basicData?.numeroNotaFiscal
                              ? `NF: ${draft.basicData.numeroNotaFiscal}`
                              : draft.formData?.fornecedor || 'Sem nota fiscal'}
                            {draft.processedProducts && Object.keys(draft.processedProducts).length > 0
                              ? ` • ${Object.keys(draft.processedProducts).length} MP processada(s)`
                              : ''}
                          </p>
                          <p className="text-xs text-gray-500">
                            Atualizado em {draft.updatedAt ? new Date(draft.updatedAt).toLocaleString('pt-BR') : '-'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleLoadDraft(draft)}
                            className="px-3 py-1 text-sm text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                          >
                            Retomar
                          </button>
                          <button
                            onClick={() => handleDeleteDraft(draft.id)}
                            className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Abas de Navegação */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('novo')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'novo'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Novo Recebimento
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('historico')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'historico'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Histórico ({recebimentosPagination.total ?? recebimentos.length})
                    </div>
                  </button>
                </nav>
              </div>
            </div>

            {/* Conteúdo da Aba: Histórico */}
            {activeTab === 'historico' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Histórico de Recebimentos</h2>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar por produto, lote, fornecedor, NF..."
                      value={searchTermHistorico}
                      onChange={(e) => setSearchTermHistorico(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="todos">Todos os Status</option>
                  <option value="aceitar">Aceitos</option>
                  <option value="rejeitar">Rejeitados</option>
                  <option value="pendente">Em Análise</option>
                </select>

                <select
                  value={filterTipoFornecedor}
                  onChange={(e) => setFilterTipoFornecedor(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  title="Filtrar por tipo de fornecedor"
                >
                  <option value="todos">Todos os tipos</option>
                  <option value="ingrediente">Ingredientes</option>
                  <option value="embalagem">Embalagens</option>
                  <option value="servico">Serviços</option>
                  <option value="limpeza_conservacao">Limpeza e conservação</option>
                </select>
                
                <div className="flex items-center gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">De</label>
                    <DateInputPt
                      value={filterDateInicio}
                      onChange={setFilterDateInicio}
                      max={filterDateFim || undefined}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Até</label>
                    <DateInputPt
                      value={filterDateFim}
                      onChange={setFilterDateFim}
                      min={filterDateInicio || undefined}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
                    />
                  </div>
                  {(filterDateInicio || filterDateFim) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilterDateInicio('');
                        setFilterDateFim('');
                      }}
                      className="mt-5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      title="Limpar datas"
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de Recebimentos */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Produto</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Fornecedor</th>
                      {isRole('superadmin') && (
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Empresa</th>
                      )}
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Lote / NF</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Quantidade</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Recebido Por</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Data</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecebimentos.map((recebimento) => (
                      <tr key={recebimento.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-gray-900">{recebimento.produto_nome || 'N/A'}</div>
                            {recebimento.produto_codigo && (
                              <div className="text-sm text-gray-500">{recebimento.produto_codigo}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-900">{recebimento.fornecedor || 'N/A'}</td>
                        {isRole('superadmin') && (
                          <td className="py-3 px-4 text-gray-900">{recebimento.empresa_nome || 'N/A'}</td>
                        )}
                        <td className="py-3 px-4 text-gray-900 max-w-[10rem]">
                          <div className="truncate" title={formatarLotesParaExibicao(
                            Array.isArray(recebimento.lote_fornecedor)
                              ? recebimento.lote_fornecedor
                              : recebimento.lote_fornecedor
                          )}>
                            {formatarLotesParaExibicao(
                              Array.isArray(recebimento.lote_fornecedor)
                                ? recebimento.lote_fornecedor
                                : recebimento.lote_fornecedor
                            )}
                          </div>
                          <div
                            className="text-xs text-gray-500 mt-0.5 truncate"
                            title={recebimento.numero_nota_fiscal ? `NF ${recebimento.numero_nota_fiscal}` : 'NF N/A'}
                          >
                            NF {recebimento.numero_nota_fiscal || 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-900 whitespace-nowrap">
                          {formatQuantidadeComUnidade(recebimento.quantidade_recebida, recebimento.unidade_medida)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            recebimento.decisao_final === 'aceitar' ? 'bg-green-100 text-green-800' :
                            recebimento.decisao_final === 'rejeitar' ? 'bg-red-100 text-red-800' :
                            recebimento.decisao_final === 'em_analise_qualidade' ? 'bg-yellow-100 text-yellow-800' :
                            recebimento.decisao_final === 'aprovado_qualidade' ? 'bg-blue-100 text-blue-800' :
                            recebimento.decisao_final === 'rejeitado_qualidade' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {recebimento.decisao_final === 'aceitar' ? 'Aceito' :
                             recebimento.decisao_final === 'rejeitar' ? 'Rejeitado' :
                             recebimento.decisao_final === 'em_analise_qualidade' ? 'Em Análise - Qualidade' :
                             recebimento.decisao_final === 'aprovado_qualidade' ? 'Aprovado - Qualidade' :
                             recebimento.decisao_final === 'rejeitado_qualidade' ? 'Rejeitado - Qualidade' :
                             recebimento.decisao_final === 'pendente' ? 'Pendente' :
                             'Em Análise'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-900">
                          {recebimento.recebidoPor?.nome || recebimento.recebido_por?.nome || 'N/A'}
                          {recebimento.recebidoPor?.email && (
                            <div className="text-xs text-gray-500">{recebimento.recebidoPor.email}</div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-900">
                          <div className="flex items-center gap-1.5">
                            <span>{formatDataRecebimento(recebimento)}</span>
                            {isRecebimentoDataRetroativa(recebimento) && (
                              <span
                                title="Data do recebimento retroativa (anterior à data do registro no sistema)"
                                className="inline-flex"
                              >
                                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => handleViewReceiving(recebimento)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Ver detalhes"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {moduloArmazenamento && recebimentoAceitoParaPallet(recebimento) && (
                              <button
                                type="button"
                                onClick={() => abrirPalletsRecebimento(recebimento)}
                                className="text-emerald-700 hover:text-emerald-900"
                                title="Pallets e etiquetas QR"
                              >
                                <Warehouse className="w-4 h-4" />
                              </button>
                            )}
                            {podeExcluirRecebimento() && (
                              <button
                                type="button"
                                onClick={() => handleExcluirRecebimento(recebimento)}
                                className="text-red-600 hover:text-red-800"
                                title="Excluir recebimento"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {recebimentosPagination.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600">
                    Mostrando {(recebimentosPagination.page - 1) * RECEBIMENTOS_PAGE_SIZE + 1} a{' '}
                    {Math.min(recebimentosPagination.page * RECEBIMENTOS_PAGE_SIZE, recebimentosPagination.total)} de{' '}
                    {recebimentosPagination.total} recebimentos
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        loadRecebimentos(recebimentosPagination.page - 1, {
                          search: searchTermHistorico,
                          status: filterStatus,
                          tipoFornecedor: filterTipoFornecedor,
                          dataInicio: filterDateInicio,
                          dataFim: filterDateFim
                        })
                      }
                      disabled={recebimentosPagination.page <= 1}
                      className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <span className="px-3 py-1.5 text-sm text-gray-600">
                      Página {recebimentosPagination.page} de {recebimentosPagination.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        loadRecebimentos(recebimentosPagination.page + 1, {
                          search: searchTermHistorico,
                          status: filterStatus,
                          tipoFornecedor: filterTipoFornecedor,
                          dataInicio: filterDateInicio,
                          dataFim: filterDateFim
                        })
                      }
                      disabled={recebimentosPagination.page >= recebimentosPagination.totalPages}
                      className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ✅ NOVO: Conteúdo da Aba: Novo Recebimento - Selecionar Fornecedor */}
            {activeTab === 'novo' && currentStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Selecionar Fornecedor para Recebimento</h2>
              
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar fornecedor..."
                      value={searchTermProduto} // ✅ REUTILIZANDO: Usar mesmo estado de busca
                      onChange={(e) => setSearchTermProduto(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filterItemsBySearch(allFornecedores, searchTermProduto, [
                      'nome_empresa',
                      'email',
                      'nome_contato',
                      'cnpj',
                      'telefone',
                    ], { matchDigits: true })
                    .map((fornecedor) => (
                      <div
                        key={fornecedor.id}
                        onClick={async () => {
                          console.log('🔍 Fornecedor selecionado:', fornecedor.nome_empresa, fornecedor.id);
                          setSelectedSupplier(fornecedor);
                          setBasicData(prev => ({
                            ...prev,
                            fornecedor: fornecedor.nome_empresa,
                            fornecedor_id: fornecedor.id
                          }));
                          // Carregar produtos do fornecedor
                          await loadSupplierProducts(fornecedor.id);
                          setCurrentStep(2);
                        }}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-1">{fornecedor.nome_empresa}</h3>
                            <p className="text-sm text-gray-500 mb-2">{fornecedor.cnpj}</p>
                          </div>
                          <Truck className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  {allFornecedores.length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum fornecedor encontrado</h3>
                      <p className="text-gray-600">
                        Não há fornecedores aprovados cadastrados. Entre em contato com o administrador.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ✅ NOVO: Step 2: Dados Básicos + Cards de Matérias-primas */}
        {currentStep === 2 && selectedSupplier && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Dados do Recebimento</h2>
                <p className="text-gray-600">Fornecedor: {selectedSupplier.nome_empresa}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSaveDraft()}
                  disabled={savingDraft}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {savingDraft ? 'Salvando...' : 'Salvar rascunho'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelRecebimento}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      !window.confirm(
                        'Voltar à seleção de fornecedor? Os dados não salvos como rascunho serão perdidos.'
                      )
                    ) {
                      return;
                    }
                    setCurrentStep(1);
                    setSelectedSupplier(null);
                    setBasicData(createInitialBasicData());
                    setSupplierProducts([]);
                    setProcessedProducts({});
                    setActiveDraftId(null);
                  }}
                  className="text-gray-600 hover:text-gray-800 flex items-center gap-2 px-3 py-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </button>
              </div>
            </div>

            {/* Seção 1: Informações Básicas */}
            <div className="mb-8">
              <h3 className="font-medium text-gray-900 mb-4 text-lg">Informações Básicas</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Fornecedor (pré-preenchido, não editável) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor
                  </label>
                  <input
                    type="text"
                    value={basicData.fornecedor}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data do Recebimento *
                  </label>
                  <DateInputPt
                    value={basicData.dataRecebimento || ''}
                    onChange={(ymd) => setBasicData((prev) => ({ ...prev, dataRecebimento: ymd }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    title="Data real em que a mercadoria foi recebida"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Pré-preenchida com a data de hoje. Altere se o processamento for em outro dia.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nº Nota Fiscal *
                  </label>
                  <input
                    type="text"
                    value={basicData.numeroNotaFiscal}
                    onChange={(e) => setBasicData(prev => ({ ...prev, numeroNotaFiscal: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nº DCPOA *
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={basicData.numeroDcpoa}
                      onChange={(e) => setBasicData(prev => ({ ...prev, numeroDcpoa: e.target.value }))}
                      disabled={basicData.dcpoaNaoSeAplica}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      required={!basicData.dcpoaNaoSeAplica}
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={basicData.dcpoaNaoSeAplica}
                        onChange={(e) => setBasicData(prev => ({ ...prev, dcpoaNaoSeAplica: e.target.checked, numeroDcpoa: e.target.checked ? '' : prev.numeroDcpoa }))}
                        className="w-4 h-4"
                      />
                      Não se aplica
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nº do Lacre *
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={basicData.numeroLacre}
                      onChange={(e) => setBasicData(prev => ({ ...prev, numeroLacre: e.target.value }))}
                      disabled={basicData.lacreNaoSeAplica}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      required={!basicData.lacreNaoSeAplica}
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={basicData.lacreNaoSeAplica}
                        onChange={(e) => setBasicData(prev => ({ ...prev, lacreNaoSeAplica: e.target.checked, numeroLacre: e.target.checked ? '' : prev.numeroLacre }))}
                        className="w-4 h-4"
                      />
                      Não se aplica
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Placa do Caminhão *
                  </label>
                  <input
                    type="text"
                    value={basicData.placaCaminhao}
                    onChange={(e) => setBasicData(prev => ({ ...prev, placaCaminhao: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ABC-1234"
                    maxLength={8}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condições de Transporte
                  </label>
                  <input
                    type="text"
                    value={basicData.condicoesTransporte}
                    onChange={(e) => setBasicData(prev => ({ ...prev, condicoesTransporte: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Refrigerado, Normal, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temperatura da Carga (°C)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={basicData.temperaturaCarga}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.,-]/g, '');
                      setBasicData(prev => ({ ...prev, temperaturaCarga: value }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 4, -18, etc."
                  />
                </div>
              </div>

              {/* Documentação fotográfica obrigatória + vídeos opcionais */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Documentação fotográfica <span className="text-red-600">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  É obrigatório enviar ao menos uma foto em cada categoria abaixo. É permitido mais de uma foto por
                  item. Fotos adicionais e vídeos são opcionais.
                </p>

                <div className="space-y-4">
                  {FOTOS_RECEBIMENTO_CATEGORIAS.map((cat) => {
                    const fotosCat =
                      normalizeFotosPorCategoria(basicData.fotos_por_categoria, basicData.fotos_gerais)[cat.id] || [];
                    const ok = fotosCat.length > 0;
                    return (
                      <div
                        key={cat.id}
                        className={`border rounded-lg p-3 ${
                          cat.obrigatoria
                            ? ok
                              ? 'border-emerald-300 bg-emerald-50/40'
                              : 'border-amber-300 bg-amber-50/30'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {cat.label}
                              {cat.obrigatoria ? <span className="text-red-600"> *</span> : null}
                            </p>
                            <p className="text-xs text-gray-500">
                              {cat.obrigatoria
                                ? ok
                                  ? `${fotosCat.length} foto(s)`
                                  : 'Pelo menos 1 foto'
                                : 'Opcional'}
                            </p>
                          </div>
                          {cat.obrigatoria && ok ? (
                            <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                          ) : null}
                        </div>

                        <div className="flex flex-wrap gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => {
                              setCameraContext('geral');
                              setCameraCategoria(cat.id);
                              startCamera('photo');
                            }}
                            className="flex-1 min-w-[140px] bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <Camera className="w-4 h-4" />
                            Tirar foto
                          </button>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => {
                              handleFileUploadForBasicData(Array.from(e.target.files || []), 'fotos', cat.id);
                              e.target.value = '';
                            }}
                            className="hidden"
                            id={`fotos-cat-${cat.id}`}
                          />
                          <label
                            htmlFor={`fotos-cat-${cat.id}`}
                            className="flex-1 min-w-[140px] flex items-center justify-center h-10 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors text-sm text-gray-600"
                          >
                            Selecionar fotos
                          </label>
                        </div>

                        {fotosCat.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {fotosCat.map((foto, index) => (
                              <div key={foto.id || `${cat.id}-${index}`} className="relative group">
                                <img
                                  src={foto.url || foto.dataUrl || foto.thumbnailUrl}
                                  alt={`${cat.label} ${index + 1}`}
                                  className="w-full h-20 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => {
                                    setViewingMedia(foto);
                                    setShowMediaModal(true);
                                  }}
                                />
                                {foto.isOffline && (
                                  <span className="absolute top-1 left-1 bg-yellow-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                    Offline
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setBasicData((prev) => {
                                      const cats = normalizeFotosPorCategoria(
                                        prev.fotos_por_categoria,
                                        prev.fotos_gerais
                                      );
                                      return {
                                        ...prev,
                                        fotos_por_categoria: {
                                          ...cats,
                                          [cat.id]: (cats[cat.id] || []).filter((_, i) => i !== index)
                                        }
                                      };
                                    });
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Vídeos Gerais (opcional) */}
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <p className="text-sm font-medium text-gray-900 mb-1">Vídeos (opcional)</p>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraContext('geral');
                          startCamera('video');
                        }}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Video className="w-4 h-4" />
                        Gravar vídeo
                      </button>
                    </div>
                    
                    <input
                      type="file"
                      multiple
                      accept="video/*"
                      onChange={(e) => handleFileUploadForBasicData(Array.from(e.target.files), 'videos')}
                      className="hidden"
                      id="videos-gerais-upload"
                    />
                    <label
                      htmlFor="videos-gerais-upload"
                      className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-3"
                    >
                      <div className="text-center">
                        <span className="text-sm text-gray-600">Selecionar vídeos</span>
                      </div>
                    </label>
                    
                    {(basicData.videos_gerais || []).length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {basicData.videos_gerais.map((video, index) => (
                          <div key={video.id || index} className="relative group">
                            <div 
                              className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => {
                                setViewingMedia(video);
                                setShowMediaModal(true);
                              }}
                            >
                              {video.thumbnailUrl ? (
                                <img
                                  src={video.thumbnailUrl}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <video
                                  src={video.url || video.dataUrl}
                                  className="w-full h-full object-cover"
                                  muted
                                  preload="metadata"
                                />
                              )}
                              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                <div className="bg-white bg-opacity-90 rounded-full p-2">
                                  <div className="w-0 h-0 border-l-[8px] border-l-blue-600 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
                                </div>
                              </div>
                            </div>
                            {video.isOffline && (
                              <span className="absolute top-1 left-1 bg-yellow-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                Offline
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setBasicData(prev => ({
                                  ...prev,
                                  videos_gerais: prev.videos_gerais.filter((_, i) => i !== index)
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Seção 2: Cards de Matérias-primas */}
            <div>
              <h3 className="font-medium text-gray-900 mb-4 text-lg">Ingredientes do Fornecedor</h3>
              
              {supplierProducts.length === 0 ? (
                <div className="text-center py-8 border border-gray-200 rounded-lg">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma matéria-prima encontrada</h3>
                  <p className="text-gray-600">
                    Este fornecedor não possui ingredientes associados. Entre em contato com o administrador.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {supplierProducts.map((produto) => {
                    const processedData = processedProducts[produto.id];
                    const processCount = processedData?.count || 0;
                    const totalVolume = processedData?.totalVolume || 0;
                    const unit = processedData?.unit || '';
                    
                    return (
                      <div
                        key={produto.id}
                        onClick={() => {
                          // Validar dados básicos antes de permitir processar
                          if (!basicData.dataRecebimento ||
                              !basicData.numeroNotaFiscal || 
                              (!basicData.numeroDcpoa && !basicData.dcpoaNaoSeAplica) ||
                              (!basicData.numeroLacre && !basicData.lacreNaoSeAplica) ||
                              !basicData.placaCaminhao) {
                            alert('Por favor, preencha todos os campos obrigatórios das informações básicas (incluindo a data do recebimento) antes de processar uma matéria-prima.');
                            return;
                          }
                          const faltandoFotos = validarFotosObrigatoriasRecebimento(basicData.fotos_por_categoria);
                          if (faltandoFotos.length > 0) {
                            alert(
                              `Fotos obrigatórias pendentes:\n• ${faltandoFotos.join('\n• ')}\n\nEnvie ao menos uma foto em cada categoria antes de processar o ingrediente.`
                            );
                            return;
                          }
                          if (processCount > 0) {
                            const ok = window.confirm(
                              `Este produto já foi processado ${processCount} vez(es) nesta NF.\n\n` +
                                'Evite duplicar o mesmo lote. Continuar?'
                            );
                            if (!ok) return;
                          }
                          // Abrir modal para dados específicos da matéria-prima
                          setCurrentProductData({
                            ...createInitialProductData(),
                            produto_id: produto.id,
                            produto_nome: produto.nome || produto.nome_produto
                          });
                          setShowProductModal(true);
                        }}
                        className="border-2 rounded-lg p-4 transition-all border-gray-200 hover:border-blue-500 hover:shadow-md cursor-pointer"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-1">{produto.nome || produto.nome_produto}</h3>
                            <p className="text-sm text-gray-500 mb-2">{produto.codigo || produto.codigo_produto || produto.codigo_origem || '—'}</p>
                            <div className="flex items-center text-xs text-gray-400 mb-2">
                              <ClipboardList className="w-3 h-3 mr-1" />
                              {(produto.parametros_qualidade || produto.parametros || []).length} parâmetro(s)
                            </div>
                            {processCount > 0 && (
                              <div className="mt-2 p-2 bg-green-100 rounded border border-green-200">
                                <p className="text-xs font-medium text-green-900">
                                  ✓ Processada {processCount} {processCount === 1 ? 'vez' : 'vezes'}
                                </p>
                                {totalVolume > 0 && (
                                  <p className="text-xs text-green-800 mt-1">
                                    Volume total: {formatQuantidadeComUnidade(totalVolume, unit)}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <Package className={`w-5 h-5 ${processCount > 0 ? 'text-green-500' : 'text-gray-400'}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ações do recebimento */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleSaveDraft()}
                    disabled={savingDraft}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                  >
                    <Save className="w-4 h-4" />
                    {savingDraft ? 'Salvando...' : 'Salvar rascunho'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelRecebimento}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                    Cancelar recebimento
                  </button>
                </div>
                {Object.keys(processedProducts).length > 0 && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (activeDraftId) {
                        try {
                          await deleteReceivingDraft(activeDraftId);
                        } catch (_) {
                          /* ignore */
                        }
                      }
                      resetRecebimentoEmAndamento();
                      await loadRecebimentos();
                      await refreshDrafts();
                    }}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Finalizar Recebimento
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ✅ MANTIDO: Step 2 antigo (compatibilidade) */}
        {/* Step 2: Coleta de Dados */}
        {currentStep === 2 && selectedProduct && !selectedSupplier && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Coleta de Dados</h2>
                <p className="text-gray-600">Produto: {selectedProduct.nome}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSaveDraft()}
                  disabled={savingDraft}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {savingDraft ? 'Salvando...' : 'Salvar rascunho'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelRecebimento}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(1);
                    setSelectedProduct(null);
                    setFormData(createInitialFormData());
                  }}
                  className="text-gray-600 hover:text-gray-800 flex items-center gap-2 px-3 py-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 mb-4">Informações Básicas</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor *
                  </label>
                  <select
                    value={formData.fornecedor}
                    onChange={(e) => setFormData(prev => ({ ...prev, fornecedor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione o fornecedor</option>
                    {fornecedores.map((fornecedor) => (
                      <option key={fornecedor.id} value={fornecedor.nome_empresa}>
                        {fornecedor.nome_empresa} - {fornecedor.cnpj}
                      </option>
                    ))}
                  </select>
                  {fornecedores.length === 0 && selectedProduct && (
                    <p className="text-sm text-yellow-600 mt-1">
                      ⚠️ Nenhum fornecedor encontrado com esta matéria prima. Verifique se há fornecedores associados a este produto.
                    </p>
                  )}
                  {fornecedores.length === 0 && !selectedProduct && (
                    <p className="text-sm text-yellow-600 mt-1">
                      ⚠️ Nenhum fornecedor aprovado encontrado. Entre em contato com o administrador.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lote(s) do Fornecedor *
                  </label>
                  <input
                    type="text"
                    value={formData.lote_fornecedor}
                    onChange={(e) => setFormData(prev => ({ ...prev, lote_fornecedor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 03001, de 03002 a 03020, 03025"
                  />
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">
                      ℹ️ Você pode informar: lote único, múltiplos lotes separados por vírgula, ou ranges (de X a Y)
                    </p>
                    <p className="text-xs text-gray-400">
                      <strong>Exemplos:</strong> "03001" | "03001, 03002, 03003" | "de 03001 a 03020" | "03001, de 03002 a 03005, 03010"
                    </p>
                    {formData.lote_fornecedor && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-1">
                          Lotes que serão criados ({processarLotes(formData.lote_fornecedor).length}):
                        </p>
                        <p className="text-xs text-blue-700">
                          {formatarLotesParaExibicao(processarLotes(formData.lote_fornecedor))}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade *
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.quantidade}
                      onChange={(e) => {
                        handleQuantidadeInput(e.target.value, (valorFormatado) => {
                          setFormData(prev => ({ ...prev, quantidade: valorFormatado }));
                        });
                      }}
                      onBlur={() => {
                        setFormData((prev) => ({
                          ...prev,
                          quantidade: formatarQuantidadeBlur(prev.quantidade)
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unidade
                    </label>
                    <select
                      value={formData.unidade}
                      onChange={(e) => setFormData(prev => ({ ...prev, unidade: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Kg">Kilograma (Kg)</option>
                      <option value="g">Gramas (g)</option>
                      <option value="Ton">Toneladas (Ton)</option>
                      <option value="L">Litros (L)</option>
                      <option value="mL">Mililitros (mL)</option>
                      <option value="Unid.">Unidade (Unid.)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Fabricação
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.dataFabricacao}
                      onChange={(e) => {
                        handleDataInput(e.target.value, (valorFormatado) => {
                          setFormData(prev => ({ ...prev, dataFabricacao: valorFormatado }));
                        });
                      }}
                      onBlur={(e) => {
                        // Validar data ao sair do campo
                        const validacao = validarData(e.target.value);
                        if (!validacao.valida && e.target.value.length >= 10) {
                          alert(validacao.mensagem || 'Data inválida');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Validade
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.dataValidade}
                      onChange={(e) => {
                        handleDataInput(e.target.value, (valorFormatado) => {
                          setFormData(prev => ({ ...prev, dataValidade: valorFormatado }));
                        });
                      }}
                      onBlur={(e) => {
                        // Validar data ao sair do campo
                        const validacao = validarData(e.target.value);
                        if (!validacao.valida && e.target.value.length >= 10) {
                          alert(validacao.mensagem || 'Data inválida');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 4 pallets, 8 caixas por pallet..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nº Nota Fiscal
                  </label>
                  <input
                    type="text"
                    value={formData.numeroNotaFiscal}
                    onChange={(e) => setFormData(prev => ({ ...prev, numeroNotaFiscal: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Informe o número da nota fiscal"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº DCPOA
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={formData.numeroDcpoa}
                        onChange={(e) => setFormData(prev => ({ ...prev, numeroDcpoa: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        placeholder="Informe o número do DCPOA"
                        disabled={formData.dcpoaNaoSeAplica}
                      />
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={formData.dcpoaNaoSeAplica}
                          onChange={(e) =>
                            setFormData(prev => ({
                              ...prev,
                              dcpoaNaoSeAplica: e.target.checked,
                              numeroDcpoa: e.target.checked ? '' : prev.numeroDcpoa
                            }))
                          }
                        />
                        N/A
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº do Lacre
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={formData.numeroLacre}
                        onChange={(e) => setFormData(prev => ({ ...prev, numeroLacre: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        placeholder="Informe o número do lacre"
                        disabled={formData.lacreNaoSeAplica}
                      />
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={formData.lacreNaoSeAplica}
                          onChange={(e) =>
                            setFormData(prev => ({
                              ...prev,
                              lacreNaoSeAplica: e.target.checked,
                              numeroLacre: e.target.checked ? '' : prev.numeroLacre
                            }))
                          }
                        />
                        N/A
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Placa do Caminhão
                  </label>
                  <input
                    type="text"
                    value={formData.placaCaminhao}
                    onChange={(e) => setFormData(prev => ({ ...prev, placaCaminhao: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: ABC1D23"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Condições de Transporte
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {[
                      { label: 'Conforme', value: 'conforme' },
                      { label: 'Não conforme', value: 'nao_conforme' }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex-1 border rounded-lg px-4 py-2 cursor-pointer flex items-center gap-2 ${
                          formData.condicoesTransporte === option.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 text-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="condicoesTransporte"
                          value={option.value}
                          checked={formData.condicoesTransporte === option.value}
                          onChange={(e) => setFormData(prev => ({ ...prev, condicoesTransporte: e.target.value }))}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temperatura da Carga (°C)
                  </label>
                  <input
                    type="text"
                    value={formatarNumeroParaExibicao(formData.temperaturaCarga)}
                    onChange={(e) => {
                      handleNumeroInput(e.target.value, (valorFormatado) => {
                        setFormData(prev => ({ ...prev, temperaturaCarga: valorFormatado }));
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 4.5"
                  />
                </div>

                {/* Upload de Mídia */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Documentação</h4>
                  
                  <div className="space-y-4">
                    {/* Captura de Mídia */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Fotos */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fotos
                        </label>
                        
                        {/* Botões de Captura */}
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={() => startCamera('photo')}
                            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <Camera className="w-4 h-4" />
                            📷 Tirar Foto
                          </button>
                        </div>
                        
                        {/* Seleção de Arquivo */}
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleFileUpload(Array.from(e.target.files), 'fotos')}
                          className="hidden"
                          id="fotos-upload"
                        />
                        <label
                          htmlFor="fotos-upload"
                          className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
                        >
                          <div className="text-center">
                            <span className="text-sm text-gray-600">📁 Selecionar Arquivo</span>
                          </div>
                        </label>
                        
                        {/* Contador e Thumbnails de Fotos */}
                        {formData.fotos.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm text-gray-600 mb-2">
                              {formData.fotos.length} foto(s) adicionada(s)
                            </div>
                            
                            {/* Grid de Thumbnails */}
                            <div className="grid grid-cols-3 gap-2">
                              {formData.fotos.map((foto, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={foto.url || foto.thumbnailUrl || foto.dataUrl}
                                    alt={`Foto ${index + 1}`}
                                    className="w-full h-20 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => openMediaViewer(foto, 'photo')}
                                  />
                                  {foto.isOffline && (
                                    <span className="absolute top-1 left-1 bg-yellow-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                      Offline
                                    </span>
                                  )}
                                  
                                  {/* Botão de Visualizar */}
                                  <button
                                    onClick={() => openMediaViewer(foto, 'photo')}
                                    className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                    title="Visualizar foto"
                                  >
                                    <Maximize2 className="w-5 h-5 text-white" />
                                  </button>
                                  
                                  {/* Botão de Remover */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeMedia('fotos', index);
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                  
                                  {/* Nome do arquivo */}
                                  <div className="text-xs text-gray-500 mt-1 truncate">
                                    {foto.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Vídeos */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Vídeos
                        </label>
                        
                        {/* Botões de Captura */}
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={() => startCamera('video')}
                            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <Video className="w-4 h-4" />
                            🎥 Gravar Vídeo
                          </button>
                        </div>
                        
                        {/* Seleção de Arquivo */}
                        <input
                          type="file"
                          multiple
                          accept="video/*"
                          onChange={(e) => handleFileUpload(Array.from(e.target.files), 'videos')}
                          className="hidden"
                          id="videos-upload"
                        />
                        <label
                          htmlFor="videos-upload"
                          className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
                        >
                          <div className="text-center">
                            <span className="text-sm text-gray-600">📁 Selecionar Arquivo</span>
                          </div>
                        </label>
                        
                        {/* Contador e Thumbnails de Vídeos */}
                        {formData.videos.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm text-gray-600 mb-2">
                              {formData.videos.length} vídeo(s) adicionado(s)
                            </div>
                            
                            {/* Grid de Thumbnails */}
                            <div className="grid grid-cols-3 gap-2">
                              {formData.videos.map((video, index) => (
                                <div key={index} className="relative group">
                                  <div 
                                    className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => openMediaViewer(video, 'video')}
                                  >
                                    {video.thumbnailUrl ? (
                                      <img
                                        src={video.thumbnailUrl}
                                        alt={`Thumbnail ${index + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <video
                                        src={video.url || video.dataUrl}
                                        className="w-full h-full object-cover"
                                        muted
                                        preload="metadata"
                                      />
                                    )}
                                    
                                    {/* Overlay de Play */}
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                      <div className="bg-white bg-opacity-90 rounded-full p-2">
                                        <div className="w-0 h-0 border-l-[8px] border-l-blue-600 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
                                      </div>
                                    </div>
                                  </div>
                                  {video.isOffline && (
                                    <span className="absolute top-1 left-1 bg-yellow-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                      Offline
                                    </span>
                                  )}
                                  
                                  {/* Botão de Remover */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeMedia('videos', index);
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                  
                                  {/* Nome do arquivo */}
                                  <div className="text-xs text-gray-500 mt-1 truncate">
                                    {video.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parâmetros de Qualidade */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 mb-4">Parâmetros de Qualidade</h3>
                
                {(selectedProduct.parametros_qualidade || selectedProduct.parametros || [])?.map((parametro, index) => { // ✅ NORMALIZADO
                  // ✅ NOVO: Detectar se é parâmetro sensorial
                  const isSensorial = parametro.tipo_limite === 'descritivo' || 
                                     parametro.padrao === 'Sensorial' ||
                                     (!parametro.valor_minimo && !parametro.valor_maximo && 
                                      !parametro.limite_minimo && !parametro.limite_maximo &&
                                      parametro.tipo_limite === 'descritivo');
                  
                  // ✅ NOVO: Obter valor do parâmetro (pode ser numérico ou objeto para sensoriais)
                  const parametroValue = formData.parametros[parametro.nome];
                  const isConforme = typeof parametroValue === 'object' && parametroValue?.conforme === true;
                  const descricaoNaoConforme = typeof parametroValue === 'object' && parametroValue?.descricao || '';
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{parametro.nome}</h4>
                        <div className="flex items-center gap-2">
                          {parametro.padrao && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {parametro.padrao}
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (parametro.obrigatorio ?? true) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {(parametro.obrigatorio ?? true) ? 'Obrigatório' : 'Opcional'}
                          </span>
                        </div>
                      </div>
                      
                      {isSensorial ? (
                        // ✅ NOVO: Interface para parâmetros sensoriais
                        <div className="space-y-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <label className="block text-xs font-medium text-blue-900 mb-1">
                              Valor Esperado:
                            </label>
                            <p className="text-sm text-blue-800">
                              {parametro.valor_esperado || 'Não especificado'}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isConforme}
                                onChange={(e) => {
                                  const newValue = e.target.checked 
                                    ? { conforme: true }
                                    : { conforme: false, descricao: '' };
                                  setFormData(prev => ({
                                    ...prev,
                                    parametros: {
                                      ...prev.parametros,
                                      [parametro.nome]: newValue
                                    }
                                  }));
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                ✓ Conforme com o esperado
                              </span>
                            </label>
                          </div>
                          
                          {!isConforme && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Descreva como está (se não conforme):
                              </label>
                              <textarea
                                value={descricaoNaoConforme}
                                onChange={(e) => {
                                  setFormData(prev => ({
                                    ...prev,
                                    parametros: {
                                      ...prev.parametros,
                                      [parametro.nome]: {
                                        conforme: false,
                                        descricao: e.target.value
                                      }
                                    }
                                  }));
                                }}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Ex: Levemente amarelada, Pontos amolecidos na superfície..."
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        // ✅ Interface para parâmetros numéricos (comportamento atual)
                        <>
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Valor Coletado</label>
                              <input
                                type="text"
                                value={typeof parametroValue === 'string' || typeof parametroValue === 'number' 
                                  ? formatarNumeroParaExibicao(parametroValue) 
                                  : ''}
                                onChange={(e) => {
                                  handleNumeroInput(e.target.value, (valorFormatado) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      parametros: {
                                        ...prev.parametros,
                                        [parametro.nome]: valorFormatado
                                      }
                                    }));
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="0,00"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Unidade</label>
                              <input
                                type="text"
                                value={parametro.unidade || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                              />
                            </div>
                          </div>

                          <div className="text-xs text-gray-500 mb-2">
                            Limites: {(parametro.valor_minimo ?? parametro.limite_minimo) !== null && (parametro.valor_minimo ?? parametro.limite_minimo) !== undefined ? formatarNumeroParaExibicao(parametro.valor_minimo ?? parametro.limite_minimo) : 'N/A'} - {(parametro.valor_maximo ?? parametro.limite_maximo) !== null && (parametro.valor_maximo ?? parametro.limite_maximo) !== undefined ? (parametro.valor_maximo === "Ausente" ? "Ausente (≤ 0)" : formatarNumeroParaExibicao(parametro.valor_maximo ?? parametro.limite_maximo)) : 'N/A'}
                          </div>

                          {validationResults[parametro.nome] && (
                            <div className={`flex items-center gap-2 text-sm ${
                              validationResults[parametro.nome].status === 'success' ? 'text-green-600' :
                              validationResults[parametro.nome].status === 'warning' ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {validationResults[parametro.nome].status === 'success' && <CheckCircle className="w-4 h-4" />}
                              {validationResults[parametro.nome].status === 'warning' && <AlertTriangle className="w-4 h-4" />}
                              {validationResults[parametro.nome].status === 'error' && <XCircle className="w-4 h-4" />}
                              {validationResults[parametro.nome].message}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <button
                onClick={() => setCurrentStep(3)}
                disabled={!formData.fornecedor || !formData.lote_fornecedor || !formData.quantidade}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                Validar Dados
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
          </div>
        )}

        {/* ✅ NOVO: Modal para Dados Específicos da Matéria-prima */}
        {showProductModal && currentProductData.produto_id && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Dados Específicos do Ingrediente</h3>
                  <p className="text-gray-600">{currentProductData.produto_nome}</p>
                </div>
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setCurrentProductData(createInitialProductData());
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Lote(s) do Fornecedor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lote(s) do Fornecedor *
                  </label>
                  <input
                    type="text"
                    value={currentProductData.lote_fornecedor}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCurrentProductData((prev) => ({
                        ...prev,
                        lote_fornecedor: value,
                        lotesQuantidades: syncLotesQuantidades(
                          value,
                          prev.lotesQuantidades,
                          prev.quantidade
                        )
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 03001, de 03002 a 03020, 03025"
                    required
                  />
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">
                      ℹ️ Você pode informar: lote único, múltiplos lotes separados por vírgula, ou ranges (de X a Y)
                    </p>
                    {currentProductData.lote_fornecedor && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-1">
                          Lotes identificados ({processarLotes(currentProductData.lote_fornecedor).length}):
                        </p>
                        <p className="text-xs text-blue-700">
                          {formatarLotesParaExibicao(processarLotes(currentProductData.lote_fornecedor))}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quantidade: total (1 lote) ou por lote (múltiplos) */}
                {(() => {
                  const lotesIdentificados = processarLotes(currentProductData.lote_fornecedor || '');
                  const multiLotes = lotesIdentificados.length > 1;
                  if (multiLotes) {
                    const totalMulti = lotesIdentificados.reduce((s, lote) => {
                      const n = parseFloat(
                        formatarNumeroParaCalculo(currentProductData.lotesQuantidades?.[lote] || '')
                      );
                      return s + (Number.isFinite(n) ? n : 0);
                    }, 0);
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Quantidade por lote * ({currentProductData.unidade || 'Kg'})
                          </label>
                          <span className="text-sm font-medium text-gray-900">
                            Total:{' '}
                            {totalMulti > 0
                              ? formatQuantidadeComUnidade(totalMulti, currentProductData.unidade)
                              : '—'}
                          </span>
                        </div>
                        <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                          {lotesIdentificados.map((lote) => (
                            <div
                              key={lote}
                              className="flex items-center gap-3 px-3 py-2 bg-white"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">Lote {lote}</p>
                              </div>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={currentProductData.lotesQuantidades?.[lote] || ''}
                                onChange={(e) => {
                                  handleQuantidadeInput(e.target.value, (valorFormatado) => {
                                    setCurrentProductData((prev) => ({
                                      ...prev,
                                      lotesQuantidades: {
                                        ...(prev.lotesQuantidades || {}),
                                        [lote]: valorFormatado
                                      }
                                    }));
                                  });
                                }}
                                onBlur={() => {
                                  setCurrentProductData((prev) => ({
                                    ...prev,
                                    lotesQuantidades: {
                                      ...(prev.lotesQuantidades || {}),
                                      [lote]: formatarQuantidadeBlur(prev.lotesQuantidades?.[lote] || '')
                                    },
                                    quantidade: (() => {
                                      const lotes = processarLotes(prev.lote_fornecedor || '');
                                      const sum = lotes.reduce((s, l) => {
                                        const n = parseFloat(
                                          formatarNumeroParaCalculo(
                                            l === lote
                                              ? formatarQuantidadeBlur(prev.lotesQuantidades?.[l] || '')
                                              : prev.lotesQuantidades?.[l] || ''
                                          )
                                        );
                                        return s + (Number.isFinite(n) ? n : 0);
                                      }, 0);
                                      return sum > 0
                                        ? sum.toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                          })
                                        : prev.quantidade;
                                    })()
                                  }));
                                }}
                                className="w-36 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                                placeholder="0,00"
                              />
                            </div>
                          ))}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                          <select
                            value={currentProductData.unidade}
                            onChange={(e) =>
                              setCurrentProductData((prev) => ({ ...prev, unidade: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="Kg">Kilograma (Kg)</option>
                            <option value="g">Gramas (g)</option>
                            <option value="Ton">Toneladas (Ton)</option>
                            <option value="L">Litros (L)</option>
                            <option value="mL">Mililitros (mL)</option>
                            <option value="Unid.">Unidade (Unid.)</option>
                          </select>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantidade *
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={currentProductData.quantidade}
                          onChange={(e) => {
                            handleQuantidadeInput(e.target.value, (valorFormatado) => {
                              setCurrentProductData((prev) => {
                                const lotes = processarLotes(prev.lote_fornecedor || '');
                                const nextMap = { ...(prev.lotesQuantidades || {}) };
                                if (lotes.length === 1) nextMap[lotes[0]] = valorFormatado;
                                return {
                                  ...prev,
                                  quantidade: valorFormatado,
                                  lotesQuantidades: nextMap
                                };
                              });
                            });
                          }}
                          onBlur={() => {
                            setCurrentProductData((prev) => {
                              const q = formatarQuantidadeBlur(prev.quantidade);
                              const lotes = processarLotes(prev.lote_fornecedor || '');
                              const nextMap = { ...(prev.lotesQuantidades || {}) };
                              if (lotes.length === 1) nextMap[lotes[0]] = q;
                              return { ...prev, quantidade: q, lotesQuantidades: nextMap };
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0,00"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unidade
                        </label>
                        <select
                          value={currentProductData.unidade}
                          onChange={(e) =>
                            setCurrentProductData((prev) => ({ ...prev, unidade: e.target.value }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="Kg">Kilograma (Kg)</option>
                          <option value="g">Gramas (g)</option>
                          <option value="Ton">Toneladas (Ton)</option>
                          <option value="L">Litros (L)</option>
                          <option value="mL">Mililitros (mL)</option>
                          <option value="Unid.">Unidade (Unid.)</option>
                        </select>
                      </div>
                    </div>
                  );
                })()}

                {/* Data de Fabricação e Validade */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Fabricação
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={currentProductData.dataFabricacao}
                      onChange={(e) => {
                        handleDataInput(e.target.value, (valorFormatado) => {
                          setCurrentProductData(prev => ({ ...prev, dataFabricacao: valorFormatado }));
                        });
                      }}
                      onBlur={(e) => {
                        // Validar data ao sair do campo
                        const validacao = validarData(e.target.value);
                        if (!validacao.valida && e.target.value.length >= 10) {
                          alert(validacao.mensagem || 'Data inválida');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Validade
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={currentProductData.dataValidade}
                      onChange={(e) => {
                        handleDataInput(e.target.value, (valorFormatado) => {
                          setCurrentProductData(prev => ({ ...prev, dataValidade: valorFormatado }));
                        });
                      }}
                      onBlur={(e) => {
                        // Validar data ao sair do campo
                        const validacao = validarData(e.target.value);
                        if (!validacao.valida && e.target.value.length >= 10) {
                          alert(validacao.mensagem || 'Data inválida');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                    />
                  </div>
                </div>

                {/* Parâmetros de Qualidade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parâmetros de Qualidade
                  </label>
                  {(() => {
                    const produtoCompleto = supplierProducts.find(p => p.id === currentProductData.produto_id);
                    const parametros = produtoCompleto?.parametros_qualidade || produtoCompleto?.parametros || [];
                    
                    if (parametros.length === 0) {
                      return (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <p className="text-sm text-gray-500">Esta matéria-prima não possui parâmetros de qualidade cadastrados.</p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-4">
                        {parametros.map((parametro, index) => {
                          // ✅ CORRIGIDO: Detecção de parâmetros sensoriais (IGUAL À VERSÃO ANTIGA)
                          const isSensorial = parametro.tipo_limite === 'descritivo' || 
                                             parametro.padrao?.toLowerCase() === 'sensorial' ||
                                             (!parametro.valor_minimo && !parametro.valor_maximo && 
                                              !parametro.limite_minimo && !parametro.limite_maximo &&
                                              parametro.tipo_limite === 'descritivo');
                          
                          const parametroValue = currentProductData.parametros[parametro.nome];
                          // ✅ Para sensoriais: objeto { conforme: boolean, descricao: string }
                          // ✅ Para numéricos: string/number
                          // ✅ Se for sensorial mas ainda não foi inicializado, tratar como não conforme
                          const isConforme = isSensorial 
                            ? (typeof parametroValue === 'object' && parametroValue?.conforme === true)
                            : false;
                          const descricaoNaoConforme = isSensorial 
                            ? (typeof parametroValue === 'object' ? (parametroValue?.descricao || '') : '')
                            : '';
                          
                          return (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-900">{parametro.nome}</h4>
                                <div className="flex items-center gap-2">
                                  {parametro.padrao && (
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {parametro.padrao}
                                    </span>
                                  )}
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    (parametro.obrigatorio ?? true) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {(parametro.obrigatorio ?? true) ? 'Obrigatório' : 'Opcional'}
                                  </span>
                                </div>
                              </div>
                              
                              {isSensorial ? (
                                // ✅ CORRIGIDO: Interface para parâmetros sensoriais (IGUAL À VERSÃO ANTIGA)
                                <div className="space-y-3">
                                  {/* ✅ Valor Esperado - SEMPRE exibir */}
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <label className="block text-xs font-medium text-blue-900 mb-1">
                                      Valor Esperado:
                                    </label>
                                    <p className="text-sm text-blue-800 whitespace-pre-wrap">
                                      {parametro.valor_esperado || parametro.valor_exato || parametro.observacao || parametro.referencia || 'Não especificado'}
                                    </p>
                                  </div>
                                  
                                  {/* ✅ Checkbox "Conforme" */}
                                  <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isConforme}
                                        onChange={(e) => {
                                          // ✅ Garantir que o objeto sempre existe
                                          const currentValue = currentProductData.parametros[parametro.nome];
                                          const currentDescricao = typeof currentValue === 'object' ? (currentValue?.descricao || '') : '';
                                          
                                          const newValue = e.target.checked 
                                            ? { conforme: true, descricao: '' }
                                            : { conforme: false, descricao: currentDescricao };
                                          setCurrentProductData(prev => ({
                                            ...prev,
                                            parametros: {
                                              ...prev.parametros,
                                              [parametro.nome]: newValue
                                            }
                                          }));
                                        }}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-sm font-medium text-gray-700">
                                        ✓ Conforme com o esperado
                                      </span>
                                    </label>
                                  </div>
                                  
                                  {/* ✅ Campo de texto - SEMPRE visível (para escrever o que observou) */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      {isConforme ? 'Observações (Opcional)' : 'Descreva o que foi observado: *'}
                                    </label>
                                    <textarea
                                      value={descricaoNaoConforme}
                                      onChange={(e) => {
                                        // ✅ Garantir que o objeto sempre existe
                                        const currentValue = currentProductData.parametros[parametro.nome];
                                        const currentConforme = typeof currentValue === 'object' && currentValue?.conforme === true;
                                        
                                        setCurrentProductData(prev => ({
                                          ...prev,
                                          parametros: {
                                            ...prev.parametros,
                                            [parametro.nome]: {
                                              conforme: currentConforme,
                                              descricao: e.target.value
                                            }
                                          }
                                        }));
                                      }}
                                      rows={3}
                                      required={!isConforme}
                                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        !isConforme && !descricaoNaoConforme ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                      }`}
                                      placeholder={isConforme 
                                        ? "Observações adicionais (opcional)..." 
                                        : "Ex: Levemente amarelada, Pontos amolecidos na superfície, Odor alterado..."}
                                    />
                                    {!isConforme && (
                                      <p className="text-xs text-red-600 mt-1">
                                        * Obrigatório quando não está conforme
                                      </p>
                                    )}
                                  </div>

                                  {/* ✅ Status de validação para parâmetros sensoriais */}
                                  {productValidationResults[parametro.nome] && (
                                    <div className={`flex items-center gap-2 text-sm p-2 rounded-lg mt-2 ${
                                      productValidationResults[parametro.nome].status === 'success' 
                                        ? 'bg-green-50 text-green-700 border border-green-200' :
                                        productValidationResults[parametro.nome].status === 'warning' 
                                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                                          'bg-red-50 text-red-700 border border-red-200'
                                    }`}>
                                      {productValidationResults[parametro.nome].status === 'success' && (
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                      )}
                                      {productValidationResults[parametro.nome].status === 'warning' && (
                                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                      )}
                                      {productValidationResults[parametro.nome].status === 'error' && (
                                        <XCircle className="w-5 h-5 text-red-600" />
                                      )}
                                      <span className="font-medium">
                                        {productValidationResults[parametro.nome].status === 'success' ? 'Conforme' :
                                         productValidationResults[parametro.nome].status === 'warning' ? 'Atenção' :
                                         'Não conforme'}
                                      </span>
                                      <span className="text-xs ml-2">
                                        - {productValidationResults[parametro.nome].message}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <div className="grid grid-cols-2 gap-4 mb-3">
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Valor Coletado</label>
                                      <input
                                        type="text"
                                        value={typeof parametroValue === 'string' || typeof parametroValue === 'number' 
                                          ? formatarNumeroParaExibicao(parametroValue) 
                                          : ''}
                                        onChange={(e) => {
                                          handleNumeroInput(e.target.value, (valorFormatado) => {
                                            setCurrentProductData(prev => ({
                                              ...prev,
                                              parametros: {
                                                ...prev.parametros,
                                                [parametro.nome]: valorFormatado
                                              }
                                            }));
                                            // ✅ Validação será recalculada automaticamente pelo useEffect
                                          });
                                        }}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                          productValidationResults[parametro.nome]?.status === 'success' ? 'border-green-300 bg-green-50' :
                                          productValidationResults[parametro.nome]?.status === 'error' ? 'border-red-300 bg-red-50' :
                                          productValidationResults[parametro.nome]?.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                                          'border-gray-300'
                                        }`}
                                        placeholder="Digite o valor"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Unidade</label>
                                      <input
                                        type="text"
                                        value={parametro.unidade || ''}
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                      />
                                    </div>
                                  </div>

                                  {/* ✅ NOVO: Exibir limites esperados */}
                                  <div className="text-xs text-gray-500 mb-2">
                                    <span className="font-medium">Limites esperados: </span>
                                    {(parametro.valor_minimo ?? parametro.limite_minimo) !== null && (parametro.valor_minimo ?? parametro.limite_minimo) !== undefined 
                                      ? formatarNumeroParaExibicao(parametro.valor_minimo ?? parametro.limite_minimo) 
                                      : 'N/A'} 
                                    {' - '}
                                    {(parametro.valor_maximo ?? parametro.limite_maximo) !== null && (parametro.valor_maximo ?? parametro.limite_maximo) !== undefined 
                                      ? (parametro.valor_maximo === "Ausente" ? "Ausente (≤ 0)" : formatarNumeroParaExibicao(parametro.valor_maximo ?? parametro.limite_maximo)) 
                                      : 'N/A'}
                                    {parametro.unidade && ` ${parametro.unidade}`}
                                  </div>

                                  {/* ✅ NOVO: Exibir status de validação visual */}
                                  {productValidationResults[parametro.nome] && (
                                    <div className={`flex items-center gap-2 text-sm p-2 rounded-lg ${
                                      productValidationResults[parametro.nome].status === 'success' 
                                        ? 'bg-green-50 text-green-700 border border-green-200' :
                                        productValidationResults[parametro.nome].status === 'warning' 
                                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                                          'bg-red-50 text-red-700 border border-red-200'
                                    }`}>
                                      {productValidationResults[parametro.nome].status === 'success' && (
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                      )}
                                      {productValidationResults[parametro.nome].status === 'warning' && (
                                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                      )}
                                      {productValidationResults[parametro.nome].status === 'error' && (
                                        <XCircle className="w-5 h-5 text-red-600" />
                                      )}
                                      <span className="font-medium">
                                        {productValidationResults[parametro.nome].status === 'success' ? 'Conforme' :
                                         productValidationResults[parametro.nome].status === 'warning' ? 'Atenção' :
                                         'Fora dos limites'}
                                      </span>
                                      <span className="text-xs ml-2">
                                        - {productValidationResults[parametro.nome].message}
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={currentProductData.observacoes}
                    onChange={(e) => setCurrentProductData(prev => ({ ...prev, observacoes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Observações específicas desta matéria-prima..."
                  />
                </div>

                {/* Fotos e Vídeos Específicos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fotos e vídeos adicionais do ingrediente (opcional)
                  </label>
                  
                  <div className="space-y-4">
                    {/* Fotos Específicas */}
                    <div>
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => {
                            setCameraContext('especifico');
                            startCamera('photo');
                          }}
                          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Camera className="w-4 h-4" />
                          📷 Tirar Foto
                        </button>
                      </div>
                      
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => handleFileUploadForProduct(Array.from(e.target.files), 'fotos')}
                        className="hidden"
                        id="fotos-especificas-upload"
                      />
                      <label
                        htmlFor="fotos-especificas-upload"
                        className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-3"
                      >
                        <div className="text-center">
                          <span className="text-sm text-gray-600">📁 Selecionar Fotos</span>
                        </div>
                      </label>
                      
                      {/* Grid de Fotos Específicas */}
                      {(currentProductData.fotos_especificas || []).length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {currentProductData.fotos_especificas.map((foto, index) => (
                            <div key={foto.id || index} className="relative group">
                              <img
                                src={foto.url || foto.dataUrl || foto.thumbnailUrl}
                                alt={`Foto ${index + 1}`}
                                className="w-full h-20 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => {
                                  setViewingMedia(foto);
                                  setShowMediaModal(true);
                                }}
                              />
                              {foto.isOffline && (
                                <span className="absolute top-1 left-1 bg-yellow-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                  Offline
                                </span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentProductData(prev => ({
                                    ...prev,
                                    fotos_especificas: prev.fotos_especificas.filter((_, i) => i !== index)
                                  }));
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Vídeos Específicos */}
                    <div>
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => {
                            setCameraContext('especifico');
                            startCamera('video');
                          }}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Video className="w-4 h-4" />
                          🎥 Gravar Vídeo
                        </button>
                      </div>
                      
                      <input
                        type="file"
                        multiple
                        accept="video/*"
                        onChange={(e) => handleFileUploadForProduct(Array.from(e.target.files), 'videos')}
                        className="hidden"
                        id="videos-especificas-upload"
                      />
                      <label
                        htmlFor="videos-especificas-upload"
                        className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors mb-3"
                      >
                        <div className="text-center">
                          <span className="text-sm text-gray-600">📁 Selecionar Vídeos</span>
                        </div>
                      </label>
                      
                      {/* Grid de Vídeos Específicos */}
                      {(currentProductData.videos_especificas || []).length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {currentProductData.videos_especificas.map((video, index) => (
                            <div key={video.id || index} className="relative group">
                              <div 
                                className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => {
                                  setViewingMedia(video);
                                  setShowMediaModal(true);
                                }}
                              >
                                {video.thumbnailUrl ? (
                                  <img
                                    src={video.thumbnailUrl}
                                    alt={`Thumbnail ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <video
                                    src={video.url || video.dataUrl}
                                    className="w-full h-full object-cover"
                                    muted
                                    preload="metadata"
                                  />
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                  <div className="bg-white bg-opacity-90 rounded-full p-2">
                                    <div className="w-0 h-0 border-l-[8px] border-l-blue-600 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
                                  </div>
                                </div>
                              </div>
                              {video.isOffline && (
                                <span className="absolute top-1 left-1 bg-yellow-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                  Offline
                                </span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentProductData(prev => ({
                                    ...prev,
                                    videos_especificas: prev.videos_especificas.filter((_, i) => i !== index)
                                  }));
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões do Modal */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setCurrentProductData(createInitialProductData());
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    // Validar campos obrigatórios
                    if (!currentProductData.lote_fornecedor || !currentProductData.quantidade) {
                      alert('Por favor, preencha os campos obrigatórios (Lote e Quantidade).');
                      return;
                    }

                    // ✅ NOVO: Calcular validação antes de mostrar decisão
                    // Forçar recálculo completo
                    const produtoCompleto = supplierProducts.find(p => p.id === currentProductData.produto_id);
                    const parametros = produtoCompleto?.parametros_qualidade || produtoCompleto?.parametros || [];
                    
                    if (parametros.length > 0) {
                      const results = {};
                      parametros.forEach(param => {
                        const valor = currentProductData.parametros[param.nome];
                        if (valor) {
                          results[param.nome] = validateParameter(param, valor);
                        }
                      });
                      setProductValidationResults(results);

                      // Calcular conformidade
                      const totalParams = parametros.length;
                      let conformeParams = 0;

                      parametros.forEach(param => {
                        const valor = currentProductData.parametros[param.nome];
                        if (valor) {
                          const isSensorial = param.tipo_limite === 'descritivo' || 
                                             param.padrao === 'Sensorial' ||
                                             (!param.valor_minimo && !param.valor_maximo && 
                                              !param.limite_minimo && !param.limite_maximo &&
                                              param.tipo_limite === 'descritivo');
                          
                          if (isSensorial && typeof valor === 'object') {
                            if (valor.conforme === true) {
                              conformeParams++;
                            }
                          } else {
                            const validation = validateParameter(param, valor);
                            if (validation.status === 'success') {
                              conformeParams++;
                            }
                          }
                        }
                      });

                      const score = totalParams > 0 ? Math.round((conformeParams / totalParams) * 100) : 0;
                      setProductConformityScore(score);

                      // Gerar recomendação
                      const hasErrors = Object.values(results).some(r => r.status === 'error');
                      const hasWarnings = Object.values(results).some(r => r.status === 'warning');

                      if (hasErrors) {
                        setProductRecommendation({
                          action: 'REJEITAR',
                          color: 'text-red-600',
                          bgColor: 'bg-red-50',
                          borderColor: 'border-red-200',
                          message: 'Produto não conforme. Parâmetros críticos fora dos limites.',
                          podeAceitar: false
                        });
                      } else if (hasWarnings && score < 80) {
                        setProductRecommendation({
                          action: 'ANALISAR',
                          color: 'text-yellow-600',
                          bgColor: 'bg-yellow-50',
                          borderColor: 'border-yellow-200',
                          message: 'Produto requer análise adicional. Alguns parâmetros fora dos limites.',
                          podeAceitar: false
                        });
                      } else if (score >= 90) {
                        setProductRecommendation({
                          action: 'ACEITAR',
                          color: 'text-green-600',
                          bgColor: 'bg-green-50',
                          borderColor: 'border-green-200',
                          message: 'Produto conforme. Todos os parâmetros dentro dos limites.',
                          podeAceitar: true
                        });
                      } else {
                        setProductRecommendation({
                          action: 'ANALISAR',
                          color: 'text-yellow-600',
                          bgColor: 'bg-yellow-50',
                          borderColor: 'border-yellow-200',
                          message: 'Produto requer análise. Conformidade parcial.',
                          podeAceitar: false
                        });
                      }
                    } else {
                      // Sem parâmetros
                      setProductValidationResults({});
                      setProductConformityScore(100);
                      setProductRecommendation({
                        action: 'ACEITAR',
                        color: 'text-green-600',
                        bgColor: 'bg-green-50',
                        borderColor: 'border-green-200',
                        message: 'Produto sem parâmetros de qualidade cadastrados.',
                        podeAceitar: true
                      });
                    }
                    
                    // ✅ NOVO: Abrir modal de decisão
                    setShowProductDecisionModal(true);
                  }}
                  disabled={loading || !currentProductData.lote_fornecedor || !currentProductData.quantidade}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    loading || !currentProductData.lote_fornecedor || !currentProductData.quantidade
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Salvando...' : 'Validar e Decidir'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ NOVO: Modal de Decisão para Matéria-prima */}
        {showProductDecisionModal && currentProductData.produto_id && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Decisão Final</h3>
                  <p className="text-gray-600">{currentProductData.produto_nome}</p>
                </div>
                <button
                  onClick={() => setShowProductDecisionModal(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Resumo do Recebimento */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-4">Resumo do Recebimento</h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Ingrediente:</span>
                    <div className="font-medium">{currentProductData.produto_nome}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Fornecedor:</span>
                    <div className="font-medium">{basicData.fornecedor}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Data do recebimento:</span>
                    <div className="font-medium">
                      {basicData.dataRecebimento
                        ? formatDataRecebimento({ data_recebimento: basicData.dataRecebimento })
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Lote(s):</span>
                    <div className="font-medium">{formatarLotesParaExibicao(processarLotes(currentProductData.lote_fornecedor))}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Quantidade:</span>
                    {(() => {
                      const { detalhes, total } = montarLotesDetalhe(
                        currentProductData.lote_fornecedor,
                        currentProductData.lotesQuantidades,
                        currentProductData.quantidade
                      );
                      if (detalhes.length > 1) {
                        return (
                          <div className="font-medium space-y-0.5">
                            {detalhes.map((d) => (
                              <div key={d.lote}>
                                {d.lote}: {formatQuantidadeComUnidade(d.quantidade, currentProductData.unidade)}
                              </div>
                            ))}
                            <div className="text-xs text-gray-600 pt-0.5">
                              Total: {formatQuantidadeComUnidade(total, currentProductData.unidade)}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="font-medium">
                          {formatQuantidadeComUnidade(currentProductData.quantidade, currentProductData.unidade)}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      productConformityScore >= 90 ? 'bg-green-500' :
                      productConformityScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                    <span className="text-sm font-medium">Conformidade: {productConformityScore}%</span>
                  </div>
                  
                  {productRecommendation && (
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${productRecommendation.bgColor} ${productRecommendation.color}`}>
                      Recomendação: {productRecommendation.action}
                    </div>
                  )}
                </div>
              </div>

              {/* Opções de Decisão */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await handleSaveProductReceiving('ACEITAR');
                      setShowProductDecisionModal(false);
                      setShowProductModal(false);
                      
                      // ✅ Atualizar tracking de processamento
                      const { total: quantidade } = montarLotesDetalhe(
                        currentProductData.lote_fornecedor,
                        currentProductData.lotesQuantidades,
                        currentProductData.quantidade
                      );
                      setProcessedProducts(prev => {
                        const produtoId = currentProductData.produto_id;
                        const existing = prev[produtoId] || { count: 0, totalVolume: 0, unit: currentProductData.unidade };
                        return {
                          ...prev,
                          [produtoId]: {
                            count: existing.count + 1,
                            totalVolume: existing.totalVolume + (quantidade || 0),
                            unit: currentProductData.unidade
                          }
                        };
                      });

                      setCurrentProductData(createInitialProductData());
                      alert('Ingrediente processado e aprovado com sucesso!');
                    } catch (error) {
                      console.error('Erro ao salvar recebimento:', error);
                      alert('Erro ao salvar recebimento: ' + (error?.details || error?.message || 'erro desconhecido'));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !productRecommendation?.podeAceitar}
                  className={`p-6 border-2 rounded-lg transition-all group ${
                    !productRecommendation?.podeAceitar 
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50' 
                      : loading 
                        ? 'border-green-200 opacity-50 cursor-wait' 
                        : 'border-green-200 hover:border-green-500 hover:bg-green-50 cursor-pointer'
                  }`}
                >
                  <div className="text-center">
                    {loading ? (
                      <div className="w-12 h-12 mx-auto mb-3 animate-spin rounded-full border-4 border-green-200 border-t-green-500"></div>
                    ) : (
                      <CheckCircle className={`w-12 h-12 mx-auto mb-3 transition-transform ${
                        !productRecommendation?.podeAceitar ? 'text-gray-400' : 'text-green-500 group-hover:scale-110'
                      }`} />
                    )}
                    <div className={`font-medium mb-2 ${!productRecommendation?.podeAceitar ? 'text-gray-500' : 'text-green-700'}`}>
                      {loading ? 'PROCESSANDO...' : !productRecommendation?.podeAceitar ? 'ACEITAR (BLOQUEADO)' : 'ACEITAR'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {!productRecommendation?.podeAceitar 
                        ? 'Parâmetros fora dos limites - requer análise' 
                        : 'Produto conforme aos padrões de qualidade'}
                    </div>
                  </div>
                </button>

                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await handleSaveProductReceiving('ANALISAR');
                      setShowProductDecisionModal(false);
                      setShowProductModal(false);
                      
                      // ✅ Atualizar tracking de processamento
                      const { total: quantidade } = montarLotesDetalhe(
                        currentProductData.lote_fornecedor,
                        currentProductData.lotesQuantidades,
                        currentProductData.quantidade
                      );
                      setProcessedProducts(prev => {
                        const produtoId = currentProductData.produto_id;
                        const existing = prev[produtoId] || { count: 0, totalVolume: 0, unit: currentProductData.unidade };
                        return {
                          ...prev,
                          [produtoId]: {
                            count: existing.count + 1,
                            totalVolume: existing.totalVolume + (quantidade || 0),
                            unit: currentProductData.unidade
                          }
                        };
                      });

                      setCurrentProductData(createInitialProductData());
                      alert('Ingrediente enviado para análise da qualidade!');
                    } catch (error) {
                      console.error('Erro ao salvar recebimento:', error);
                      alert('Erro ao salvar recebimento: ' + (error?.details || error?.message || 'erro desconhecido'));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className={`p-6 border-2 border-yellow-200 rounded-lg hover:border-yellow-500 hover:bg-yellow-50 transition-all group ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                >
                  <div className="text-center">
                    {loading ? (
                      <div className="w-12 h-12 mx-auto mb-3 animate-spin rounded-full border-4 border-yellow-200 border-t-yellow-500"></div>
                    ) : (
                      <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    )}
                    <div className="font-medium text-yellow-700 mb-2">
                      {loading ? 'PROCESSANDO...' : 'ANALISAR'}
                    </div>
                    <div className="text-sm text-gray-600">
                      Produto requer análise adicional
                    </div>
                  </div>
                </button>

                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await handleSaveProductReceiving('REJEITAR');
                      setShowProductDecisionModal(false);
                      setShowProductModal(false);
                      
                      // ✅ Atualizar tracking de processamento
                      const { total: quantidade } = montarLotesDetalhe(
                        currentProductData.lote_fornecedor,
                        currentProductData.lotesQuantidades,
                        currentProductData.quantidade
                      );
                      setProcessedProducts(prev => {
                        const produtoId = currentProductData.produto_id;
                        const existing = prev[produtoId] || { count: 0, totalVolume: 0, unit: currentProductData.unidade };
                        return {
                          ...prev,
                          [produtoId]: {
                            count: existing.count + 1,
                            totalVolume: existing.totalVolume + (quantidade || 0),
                            unit: currentProductData.unidade
                          }
                        };
                      });

                      setCurrentProductData(createInitialProductData());
                      alert('Ingrediente rejeitado e registrado!');
                    } catch (error) {
                      console.error('Erro ao salvar recebimento:', error);
                      alert('Erro ao salvar recebimento: ' + (error?.details || error?.message || 'erro desconhecido'));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className={`p-6 border-2 border-red-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all group ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                >
                  <div className="text-center">
                    {loading ? (
                      <div className="w-12 h-12 mx-auto mb-3 animate-spin rounded-full border-4 border-red-200 border-t-red-500"></div>
                    ) : (
                      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    )}
                    <div className="font-medium text-red-700 mb-2">
                      {loading ? 'PROCESSANDO...' : 'REJEITAR'}
                    </div>
                    <div className="text-sm text-gray-600">
                      Produto não conforme - devolver
                    </div>
                  </div>
                </button>
              </div>

              {/* Observações Finais */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações da Decisão (Opcional)
                </label>
                <textarea
                  value={currentProductData.observacoes}
                  onChange={(e) => setCurrentProductData(prev => ({ ...prev, observacoes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Justificativa ou observações adicionais sobre a decisão..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Validação */}
        {currentStep === 3 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Validação de Conformidade</h2>
                <p className="text-gray-600">Análise automática dos parâmetros coletados</p>
              </div>
              <button
                onClick={() => setCurrentStep(2)}
                className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
            </div>

            {/* Score de Conformidade */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="text-center">
                <div className={`text-4xl font-bold mb-2 ${
                  conformityScore >= 90 ? 'text-green-600' :
                  conformityScore >= 70 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {conformityScore}%
                </div>
                <div className="text-gray-600 mb-4">Conformidade Geral</div>
                
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      conformityScore >= 90 ? 'bg-green-500' :
                      conformityScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${conformityScore}%` }}
                  ></div>
                </div>

                {recommendation && (
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg border ${recommendation.bgColor} ${recommendation.borderColor}`}>
                    <span className={`font-medium ${recommendation.color}`}>
                      {recommendation.action}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Detalhes da Validação */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Detalhes da Validação</h3>
              
              {(selectedProduct.parametros || selectedProduct.parametros_qualidade)?.map((parametro, index) => {
                const valor = formData.parametros[parametro.nome];
                const validation = validationResults[parametro.nome];
                
                // ✅ NOVO: Detectar se é parâmetro sensorial
                const isSensorial = parametro.tipo_limite === 'descritivo' || 
                                   parametro.padrao === 'Sensorial' ||
                                   (!parametro.valor_minimo && !parametro.valor_maximo && 
                                    !parametro.limite_minimo && !parametro.limite_maximo &&
                                    parametro.tipo_limite === 'descritivo');
                
                // ✅ NOVO: Formatar valor para exibição
                let valorExibicao = 'N/A';
                if (isSensorial && typeof valor === 'object') {
                  if (valor.conforme === true) {
                    valorExibicao = '✓ Conforme';
                  } else if (valor.descricao) {
                    valorExibicao = valor.descricao;
                  }
                } else if (valor) {
                  // ✅ CORREÇÃO: Formatar número com vírgula para exibição
                  const valorFormatado = formatarNumeroParaExibicao(valor);
                  valorExibicao = `${valorFormatado} ${parametro.unidade || ''}`.trim();
                }
                
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{parametro.nome}</h4>
                        {parametro.padrao && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {parametro.padrao}
                          </span>
                        )}
                      </div>
                      {validation && (
                        <div className={`flex items-center gap-2 ${
                          validation.status === 'success' ? 'text-green-600' :
                          validation.status === 'warning' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {validation.status === 'success' && <CheckCircle className="w-5 h-5" />}
                          {validation.status === 'warning' && <AlertTriangle className="w-5 h-5" />}
                          {validation.status === 'error' && <XCircle className="w-5 h-5" />}
                        </div>
                      )}
                    </div>
                    
                    {isSensorial ? (
                      // ✅ NOVO: Exibição para parâmetros sensoriais
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Valor Esperado:</span>
                          <div className="font-medium text-blue-800 bg-blue-50 p-2 rounded mt-1">
                            {parametro.valor_esperado || 'Não especificado'}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Avaliação:</span>
                          <div className="font-medium mt-1">{valorExibicao}</div>
                        </div>
                      </div>
                    ) : (
                      // ✅ Exibição para parâmetros numéricos
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Valor Coletado:</span>
                          <div className="font-medium">{valorExibicao}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Limite Mínimo:</span>
                          <div className="font-medium">
                            {(parametro.valor_minimo ?? parametro.limite_minimo) !== null && (parametro.valor_minimo ?? parametro.limite_minimo) !== undefined 
                              ? formatarNumeroParaExibicao(parametro.valor_minimo ?? parametro.limite_minimo) 
                              : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Limite Máximo:</span>
                          <div className="font-medium">
                            {(parametro.valor_maximo ?? parametro.limite_maximo) !== null && (parametro.valor_maximo ?? parametro.limite_maximo) !== undefined 
                              ? (parametro.valor_maximo === "Ausente" ? "Ausente (≤ 0)" : formatarNumeroParaExibicao(parametro.valor_maximo ?? parametro.limite_maximo)) 
                              : 'N/A'}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {validation && (
                      <div className={`mt-3 text-sm ${
                        validation.status === 'success' ? 'text-green-600' :
                        validation.status === 'warning' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {validation.message}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Recomendação */}
            {recommendation && (
              <div className={`mt-6 p-4 rounded-lg border ${recommendation.bgColor} ${recommendation.borderColor}`}>
                <div className="flex items-center gap-3">
                  {recommendation.action === 'ACEITAR' && <CheckCircle className={`w-6 h-6 ${recommendation.color}`} />}
                  {recommendation.action === 'ANALISAR' && <AlertTriangle className={`w-6 h-6 ${recommendation.color}`} />}
                  {recommendation.action === 'REJEITAR' && <XCircle className={`w-6 h-6 ${recommendation.color}`} />}
                  <div>
                    <div className={`font-medium ${recommendation.color}`}>
                      Recomendação: {recommendation.action}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {recommendation.message}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-8">
              <button
                onClick={() => setCurrentStep(4)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                Tomar Decisão
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Decisão */}
        {currentStep === 4 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Decisão Final</h2>
                <p className="text-gray-600">Escolha a ação para este recebimento</p>
              </div>
              <button
                onClick={() => setCurrentStep(3)}
                className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
            </div>

            {/* Resumo do Recebimento */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-medium text-gray-900 mb-4">Resumo do Recebimento</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Produto:</span>
                  <div className="font-medium">{selectedProduct.nome}</div>
                </div>
                <div>
                  <span className="text-gray-500">Fornecedor:</span>
                  <div className="font-medium">{formData.fornecedor}</div>
                </div>
                <div>
                  <span className="text-gray-500">Lote(s) do Fornecedor:</span>
                  <div className="font-medium">{formatarLotesParaExibicao(processarLotes(formData.lote_fornecedor))}</div>
                </div>
                <div>
                  <span className="text-gray-500">Quantidade:</span>
                  <div className="font-medium">{formatQuantidadeComUnidade(formData.quantidade, formData.unidade)}</div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    conformityScore >= 90 ? 'bg-green-500' :
                    conformityScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}></div>
                  <span className="text-sm font-medium">Conformidade: {conformityScore}%</span>
                </div>
                
                {recommendation && (
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${recommendation.bgColor} ${recommendation.color}`}>
                    Recomendação: {recommendation.action}
                  </div>
                )}
              </div>
            </div>

            {/* Opções de Decisão */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => recommendation?.podeAceitar && !loading && handleFinishReceiving('ACEITAR')}
                disabled={loading || !recommendation?.podeAceitar}
                className={`p-6 border-2 rounded-lg transition-all group ${
                  !recommendation?.podeAceitar 
                    ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50' 
                    : loading 
                      ? 'border-green-200 opacity-50 cursor-wait' 
                      : 'border-green-200 hover:border-green-500 hover:bg-green-50 cursor-pointer'
                }`}
              >
                <div className="text-center">
                  {loading ? (
                    <div className="w-12 h-12 mx-auto mb-3 animate-spin rounded-full border-4 border-green-200 border-t-green-500"></div>
                  ) : (
                    <CheckCircle className={`w-12 h-12 mx-auto mb-3 transition-transform ${
                      !recommendation?.podeAceitar ? 'text-gray-400' : 'text-green-500 group-hover:scale-110'
                    }`} />
                  )}
                  <div className={`font-medium mb-2 ${!recommendation?.podeAceitar ? 'text-gray-500' : 'text-green-700'}`}>
                    {loading ? 'PROCESSANDO...' : !recommendation?.podeAceitar ? 'ACEITAR (BLOQUEADO)' : 'ACEITAR'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {!recommendation?.podeAceitar 
                      ? 'Parâmetros fora dos limites - requer análise' 
                      : 'Produto conforme aos padrões de qualidade'}
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleFinishReceiving('ANALISAR')}
                disabled={loading}
                className={`p-6 border-2 border-yellow-200 rounded-lg hover:border-yellow-500 hover:bg-yellow-50 transition-all group ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
              >
                <div className="text-center">
                  {loading ? (
                    <div className="w-12 h-12 mx-auto mb-3 animate-spin rounded-full border-4 border-yellow-200 border-t-yellow-500"></div>
                  ) : (
                    <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  )}
                  <div className="font-medium text-yellow-700 mb-2">
                    {loading ? 'PROCESSANDO...' : 'ANALISAR'}
                  </div>
                  <div className="text-sm text-gray-600">
                    Produto requer análise adicional
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleFinishReceiving('REJEITAR')}
                disabled={loading}
                className={`p-6 border-2 border-red-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all group ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
              >
                <div className="text-center">
                  {loading ? (
                    <div className="w-12 h-12 mx-auto mb-3 animate-spin rounded-full border-4 border-red-200 border-t-red-500"></div>
                  ) : (
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  )}
                  <div className="font-medium text-red-700 mb-2">
                    {loading ? 'PROCESSANDO...' : 'REJEITAR'}
                  </div>
                  <div className="text-sm text-gray-600">
                    Produto não conforme - devolver
                  </div>
                </div>
              </button>
            </div>

            {/* Observações Finais */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações da Decisão (Opcional)
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Justificativa ou observações adicionais sobre a decisão..."
              />
            </div>
          </div>
        )}

        {/* Modal de Captura de Câmera */}
        {isCameraActive && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
              {/* Header do Modal */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {mediaType === 'photo' ? '📷 Capturar Foto' : '🎥 Gravar Vídeo'}
                </h3>
                <button
                  onClick={stopCamera}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Área da Câmera */}
              <div className="relative bg-black">
                {cameraStream && (
                  <video
                    ref={(video) => {
                      if (video) {
                        video.srcObject = cameraStream;
                        video.play();
                      }
                    }}
                    className="w-full h-64 object-cover"
                    style={{ transform: `scale(${cameraZoom})` }}
                    autoPlay
                    muted
                    playsInline
                  />
                )}
                
                {/* Controles de Zoom (canto superior direito) */}
                {mediaType === 'photo' && (
                  <div className="absolute top-4 right-4 flex flex-col gap-2 bg-black bg-opacity-50 rounded-lg p-2">
                    <button
                      onClick={() => adjustCameraZoom(0.1)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded p-2 transition-colors"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-5 h-5 text-white" />
                    </button>
                    <div className="text-white text-xs text-center px-2">
                      {Math.round(cameraZoom * 100)}%
                    </div>
                    <button
                      onClick={() => adjustCameraZoom(-0.1)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded p-2 transition-colors"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-5 h-5 text-white" />
                    </button>
                  </div>
                )}
                
                {/* Overlay de Controles */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
                  {mediaType === 'photo' ? (
                    <button
                      onClick={capturePhoto}
                      className="bg-white rounded-full p-4 hover:bg-gray-100 transition-colors shadow-lg"
                    >
                      <div className="w-8 h-8 bg-blue-600 rounded-full border-4 border-white"></div>
                    </button>
                  ) : (
                    <div className="flex items-center gap-4">
                      {!isRecording ? (
                        <button
                          onClick={startVideoRecording}
                          className="bg-red-600 text-white rounded-full p-4 hover:bg-red-700 transition-colors shadow-lg"
                        >
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </button>
                      ) : (
                        <button
                          onClick={stopVideoRecording}
                          className="bg-gray-600 text-white rounded-full p-4 hover:bg-gray-700 transition-colors shadow-lg"
                        >
                          <div className="w-4 h-4 bg-white rounded"></div>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Preview da Mídia Capturada */}
              {capturedMedia && (
                <div className="p-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Preview:</h4>
                  
                  {mediaType === 'photo' ? (
                    <div className="text-center">
                      <img
                        src={URL.createObjectURL(capturedMedia)}
                        alt="Foto capturada"
                        className="max-w-full h-32 object-cover rounded-lg mx-auto"
                      />
                    </div>
                  ) : (
                    <div className="text-center">
                      <video
                        src={URL.createObjectURL(capturedMedia)}
                        controls
                        className="max-w-full h-32 object-cover rounded-lg mx-auto"
                      />
                    </div>
                  )}
                  
                  {/* Botões de Ação */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={saveCapturedMedia}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      ✅ Salvar
                    </button>
                    <button
                      onClick={() => setCapturedMedia(null)}
                      className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      🔄 Nova Captura
                    </button>
                    <button
                      onClick={stopCamera}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      ❌ Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Instruções */}
              {!capturedMedia && (
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-600 text-center">
                    {mediaType === 'photo' ? (
                      <p>📷 Posicione o produto na tela e clique no botão para capturar a foto</p>
                    ) : (
                      <p>🎥 Clique no botão vermelho para iniciar a gravação e novamente para parar</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de Visualização de Recebimento */}
        {showViewModal && viewingReceiving && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Detalhes do Recebimento</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Informações Básicas</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div><span className="font-medium">Produto:</span> {viewingReceiving.produto_nome || 'N/A'}</div>
                    {viewingReceiving.produto_codigo && (
                      <div><span className="font-medium">Código do produto:</span> {viewingReceiving.produto_codigo}</div>
                    )}
                    <div><span className="font-medium">Fornecedor:</span> {viewingReceiving.fornecedor || 'N/A'}</div>
                    <div>
                      <span className="font-medium">Lote(s) Fornecedor:</span>{' '}
                      {formatarLotesParaExibicao(
                        Array.isArray(viewingReceiving.lote_fornecedor)
                          ? viewingReceiving.lote_fornecedor
                          : viewingReceiving.lote_fornecedor
                      )}
                      {Array.isArray(viewingReceiving.lotes_detalhe) &&
                        viewingReceiving.lotes_detalhe.length > 0 && (
                          <ul className="mt-1 ml-4 text-sm text-gray-700 list-disc">
                            {viewingReceiving.lotes_detalhe.map((item, idx) => (
                              <li key={`${item.lote}-${idx}`}>
                                Lote {item.lote}:{' '}
                                {formatQuantidadeComUnidade(
                                  item.quantidade,
                                  viewingReceiving.unidade_medida
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                    <div>
                      <span className="font-medium">Nº Nota Fiscal:</span>{' '}
                      {viewingReceiving.numero_nota_fiscal || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Nº DCPOA:</span>{' '}
                      {viewingReceiving.dcpoa_nao_se_aplica
                        ? 'Não se aplica'
                        : (viewingReceiving.numero_dcpoa || 'N/A')}
                    </div>
                    <div>
                      <span className="font-medium">Nº Lacre:</span>{' '}
                      {viewingReceiving.lacre_nao_se_aplica
                        ? 'Não se aplica'
                        : (viewingReceiving.numero_lacre || 'N/A')}
                    </div>
                    <div>
                      <span className="font-medium">Placa do caminhão:</span>{' '}
                      {viewingReceiving.placa_caminhao || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Condições de transporte:</span>{' '}
                      {viewingReceiving.condicoes_transporte || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Temperatura da carga:</span>{' '}
                      {viewingReceiving.temperatura_carga != null && viewingReceiving.temperatura_carga !== ''
                        ? `${formatQuantidade(viewingReceiving.temperatura_carga)} °C`
                        : 'N/A'}
                    </div>
                    {podeEditarQuantidadeUnidade() && viewingReceiving.status !== 'cancelado' ? (
                      <div className="space-y-2 pt-1 border border-blue-200 bg-white rounded-lg p-3">
                        <div className="text-sm font-medium text-gray-900">Quantidade e unidade (editável)</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={editViewQuantidade}
                              onChange={(e) => {
                                handleQuantidadeInput(e.target.value, setEditViewQuantidade);
                              }}
                              onBlur={() => {
                                setEditViewQuantidade((prev) => formatarQuantidadeBlur(prev));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                              placeholder="0,00"
                              disabled={savingViewQuantidade}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                            <select
                              value={editViewUnidade}
                              onChange={(e) => setEditViewUnidade(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                              disabled={savingViewQuantidade}
                            >
                              <option value="Kg">Kilograma (Kg)</option>
                              <option value="g">Gramas (g)</option>
                              <option value="Ton">Toneladas (Ton)</option>
                              <option value="L">Litros (L)</option>
                              <option value="mL">Mililitros (mL)</option>
                              <option value="Unid.">Unidade (Unid.)</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleSalvarQuantidadeUnidadeHistorico}
                          disabled={savingViewQuantidade}
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                        >
                          <Save className="h-4 w-4" />
                          {savingViewQuantidade ? 'Salvando...' : 'Salvar quantidade e unidade'}
                        </button>
                      </div>
                    ) : (
                      <div><span className="font-medium">Quantidade:</span> {formatQuantidadeComUnidade(viewingReceiving.quantidade_recebida, viewingReceiving.unidade_medida)}</div>
                    )}
                    <div><span className="font-medium">Data Fabricação:</span> {(() => {
                      try {
                        if (viewingReceiving.data_fabricacao) {
                          const date = viewingReceiving.data_fabricacao instanceof Date 
                            ? viewingReceiving.data_fabricacao 
                            : new Date(viewingReceiving.data_fabricacao);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('pt-BR');
                          }
                        }
                        return 'N/A';
                      } catch (error) {
                        console.error('Erro ao formatar data fabricação:', error);
                        return 'N/A';
                      }
                    })()}</div>
                    <div><span className="font-medium">Data Validade:</span> {(() => {
                      try {
                        if (viewingReceiving.data_validade) {
                          const date = viewingReceiving.data_validade instanceof Date 
                            ? viewingReceiving.data_validade 
                            : new Date(viewingReceiving.data_validade);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('pt-BR');
                          }
                        }
                        return 'N/A';
                      } catch (error) {
                        console.error('Erro ao formatar data validade:', error);
                        return 'N/A';
                      }
                    })()}</div>
                    {podeEditarQuantidadeUnidade() && viewingReceiving.status !== 'cancelado' ? (
                      <div className="space-y-2 pt-1 border border-blue-200 bg-white rounded-lg p-3">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                          Data do recebimento (editável)
                          {isRecebimentoDataRetroativa({
                            ...viewingReceiving,
                            data_recebimento: editViewDataRecebimento || viewingReceiving.data_recebimento
                          }) && (
                            <span title="Data retroativa em relação ao registro no sistema">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            </span>
                          )}
                        </div>
                        <DateInputPt
                          value={editViewDataRecebimento}
                          onChange={setEditViewDataRecebimento}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          disabled={savingViewDataRecebimento}
                          title="Data real do recebimento"
                        />
                        <button
                          type="button"
                          onClick={handleSalvarDataRecebimentoHistorico}
                          disabled={savingViewDataRecebimento}
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                        >
                          <Save className="h-4 w-4" />
                          {savingViewDataRecebimento ? 'Salvando...' : 'Salvar data do recebimento'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">Data do Recebimento:</span>{' '}
                        {formatDataRecebimento(viewingReceiving)}
                        {isRecebimentoDataRetroativa(viewingReceiving) && (
                          <span title="Data do recebimento retroativa (anterior à data do registro no sistema)">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          </span>
                        )}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Registrado em:</span>{' '}
                      {(() => {
                        try {
                          const date = parseAnyDate(viewingReceiving.criado_em);
                          if (!date) return 'N/A';
                          return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}`;
                        } catch (error) {
                          console.error('Erro ao formatar data criação:', error);
                          return 'N/A';
                        }
                      })()}
                    </div>
                    <div>
                      <span className="font-medium">Recebido Por:</span> {viewingReceiving.recebidoPor?.nome || viewingReceiving.recebido_por?.nome || 'N/A'}
                      {viewingReceiving.recebidoPor?.email && (
                        <div className="text-sm text-gray-500 ml-4">{viewingReceiving.recebidoPor.email}</div>
                      )}
                    </div>
                    {viewingReceiving.observacoes_recebedor && (
                      <div><span className="font-medium">Observações:</span> {viewingReceiving.observacoes_recebedor}</div>
                    )}
                  </div>
                </div>

                {/* Análise de Qualidade */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Análise de Qualidade</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        (viewingReceiving.analise_conformidade?.percentual_conformidade || 0) >= 90 ? 'bg-green-500' :
                        (viewingReceiving.analise_conformidade?.percentual_conformidade || 0) >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}></div>
                      <span className="font-medium">Qualidade: {viewingReceiving.analise_conformidade?.percentual_conformidade || 0}%</span>
                    </div>
                    <div><span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                        viewingReceiving.decisao_final === 'aceitar' ? 'bg-green-100 text-green-800' :
                        viewingReceiving.decisao_final === 'rejeitar' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {viewingReceiving.decisao_final === 'aceitar' ? 'Aceito' :
                         viewingReceiving.decisao_final === 'rejeitar' ? 'Rejeitado' : 'Em Análise'}
                      </span>
                    </div>
                    {viewingReceiving.analise_conformidade?.recomendacoes && (
                      <div>
                        <span className="font-medium">Recomendações:</span>
                        <ul className="mt-1 text-sm text-gray-600">
                          {viewingReceiving.analise_conformidade.recomendacoes.map((rec, index) => (
                            <li key={index}>• {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Parâmetros Analisados */}
                {(viewingReceiving.parametros_coletados || viewingReceiving.analise_conformidade?.parametros_analisados) && (
                  <div className="md:col-span-2 space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Parâmetros Coletados</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* ✅ MELHORADO: Usar parametros_analisados se disponível, senão usar parametros_coletados */}
                        {viewingReceiving.analise_conformidade?.parametros_analisados ? (
                          viewingReceiving.analise_conformidade.parametros_analisados.map((param, index) => {
                            const status = param.status || 'nao_analisado';
                            const valorColetado = param.valor_coletado;
                            
                            // Formatar valor coletado
                            let valorExibicao = 'N/A';
                            if (valorColetado !== null && valorColetado !== undefined) {
                              if (typeof valorColetado === 'object' && valorColetado.conforme !== undefined) {
                                valorExibicao = valorColetado.conforme ? '✓ Conforme' : (valorColetado.descricao || 'Não conforme');
                              } else {
                                valorExibicao = `${valorColetado} ${param.unidade || ''}`.trim();
                              }
                            }
                            
                            return (
                              <div 
                                key={index} 
                                className={`border-2 rounded-lg p-4 ${
                                  status === 'conforme' ? 'border-green-500 bg-green-50' :
                                  status === 'atencao' ? 'border-yellow-500 bg-yellow-50' :
                                  status === 'nao_conforme' ? 'border-red-500 bg-red-50' :
                                  'border-gray-300 bg-white'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <span className="font-medium text-gray-900">{param.nome}</span>
                                  <div className={`flex items-center gap-2 ${
                                    status === 'conforme' ? 'text-green-600' :
                                    status === 'atencao' ? 'text-yellow-600' :
                                    status === 'nao_conforme' ? 'text-red-600' :
                                    'text-gray-500'
                                  }`}>
                                    {status === 'conforme' && <CheckCircle className="w-5 h-5" />}
                                    {status === 'atencao' && <AlertTriangle className="w-5 h-5" />}
                                    {status === 'nao_conforme' && <XCircle className="w-5 h-5" />}
                                    <span className="text-xs font-medium">
                                      {status === 'conforme' ? 'Conforme' :
                                       status === 'atencao' ? 'ATENÇÃO' :
                                       status === 'nao_conforme' ? 'FORA' :
                                       'N/A'}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                  <div>
                                    <span className="text-gray-600">Valor Coletado: </span>
                                    <span className="font-medium text-gray-900">{valorExibicao}</span>
                                  </div>
                                  
                                  {(param.valor_minimo !== null && param.valor_minimo !== undefined) || 
                                   (param.valor_maximo !== null && param.valor_maximo !== undefined) ? (
                                    <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-200">
                                      {param.valor_minimo !== null && param.valor_minimo !== undefined && (
                                        <div>
                                          <span className="text-gray-500 text-xs">Mínimo: </span>
                                          <span className="text-xs font-medium">{param.valor_minimo} {param.unidade || ''}</span>
                                        </div>
                                      )}
                                      {param.valor_maximo !== null && param.valor_maximo !== undefined && (
                                        <div>
                                          <span className="text-gray-500 text-xs">Máximo: </span>
                                          <span className="text-xs font-medium">{param.valor_maximo} {param.unidade || ''}</span>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                  
                                  {param.observacoes && (
                                    <div className={`mt-2 pt-2 border-t border-gray-200 text-xs ${
                                      status === 'conforme' ? 'text-green-700' :
                                      status === 'atencao' ? 'text-yellow-700' :
                                      status === 'nao_conforme' ? 'text-red-700' :
                                      'text-gray-600'
                                    }`}>
                                      {param.observacoes}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          // Fallback: usar parametros_coletados se parametros_analisados não estiver disponível
                          Object.entries(viewingReceiving.parametros_coletados).map(([nome, valor], index) => (
                            <div key={index} className="border rounded p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">{nome}</span>
                              </div>
                              <div className="text-sm text-gray-600">
                                <div>Valor: {typeof valor === 'object' && valor.conforme !== undefined 
                                  ? (valor.conforme ? '✓ Conforme' : (valor.descricao || 'Não conforme'))
                                  : String(valor)}</div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Fotos e Vídeos */}
                {(viewingReceiving.fotos?.length > 0 || viewingReceiving.videos?.length > 0) && (
                  <div className="md:col-span-2 space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Documentação</h3>
                    
                    {/* Fotos agrupadas por categoria */}
                    {viewingReceiving.fotos?.length > 0 && (
                      <div className="mb-4 space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">
                          Fotos ({viewingReceiving.fotos.length})
                        </h4>
                        {(() => {
                          const grupos = {};
                          viewingReceiving.fotos.forEach((foto, index) => {
                            const cat =
                              typeof foto === 'object' && foto !== null && foto.categoria
                                ? foto.categoria
                                : 'legado';
                            if (!grupos[cat]) grupos[cat] = [];
                            grupos[cat].push({ foto, index });
                          });
                          const ordem = [
                            ...FOTOS_RECEBIMENTO_CATEGORIAS.map((c) => c.id),
                            'legado'
                          ];
                          const ids = [
                            ...ordem.filter((id) => grupos[id]?.length),
                            ...Object.keys(grupos).filter((id) => !ordem.includes(id))
                          ];
                          return ids.map((catId) => (
                            <div key={catId}>
                              <p className="text-xs font-medium text-gray-600 mb-2">
                                {catId === 'legado' ? 'Sem categoria (legado)' : labelCategoriaFoto(catId)}
                              </p>
                              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                {grupos[catId].map(({ foto, index }) => (
                                  <div
                                    key={index}
                                    className="relative group cursor-pointer"
                                    onClick={() =>
                                      openMediaViewer(foto, 'photo', viewingReceiving.fotos, index)
                                    }
                                  >
                                    <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                                      <img
                                        src={
                                          typeof foto === 'string'
                                            ? foto
                                            : foto?.url || foto?.thumbnailUrl || foto
                                        }
                                        alt={`Foto ${index + 1}`}
                                        className="max-w-full max-h-full object-contain hover:opacity-80 transition-opacity"
                                        onError={(e) => {
                                          console.error('Erro ao carregar thumbnail da foto:', foto);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                      <Maximize2 className="w-5 h-5 text-white" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                    
                    {/* Vídeos */}
                    {viewingReceiving.videos?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Vídeos ({viewingReceiving.videos.length})</h4>
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                          {viewingReceiving.videos.map((video, index) => (
                            <div 
                              key={index} 
                              className="relative group cursor-pointer"
                              onClick={() => openMediaViewer(video, 'video', viewingReceiving.videos, index)}
                            >
                              <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                                {(video.thumbnailUrl || (typeof video === 'string' ? false : video?.url)) ? (
                                  <img
                                    src={typeof video === 'string' ? video : (video.thumbnailUrl || video.url || video)}
                                    alt={`Thumbnail ${index + 1}`}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => {
                                      console.error('Erro ao carregar thumbnail do vídeo:', video);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                    <Video className="w-8 h-8 text-gray-400" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                  <div className="bg-white bg-opacity-90 rounded-full p-2">
                                    <div className="w-0 h-0 border-l-[8px] border-l-blue-600 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
                                  </div>
                                </div>
                              </div>
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                <Maximize2 className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>

              <div className="mt-6 flex justify-between gap-2">
                {podeExcluirRecebimento() && (
                  <button
                    type="button"
                    onClick={() => handleExcluirRecebimento(viewingReceiving)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir recebimento
                  </button>
                )}
                {moduloArmazenamento && recebimentoAceitoParaPallet(viewingReceiving) && (
                  <button
                    type="button"
                    onClick={() => abrirPalletsRecebimento(viewingReceiving)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    <Warehouse className="w-4 h-4" />
                    Pallets e etiquetas
                  </button>
                )}
                <button
                  onClick={() => setShowViewModal(false)}
                  className="ml-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Visualização de Mídia */}
        {showMediaModal && viewingMedia && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4">
            <div className="relative max-w-7xl w-full max-h-[95vh] flex flex-col items-center">
              <button
                onClick={() => {
                  setShowMediaModal(false);
                  setViewingMedia(null);
                  setMediaGallery([]);
                }}
                className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-2 z-10 transition-colors"
                title="Fechar"
              >
                <X className="w-6 h-6" />
              </button>

              {mediaGallery.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => navigateMediaGallery(-1)}
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/35 text-white rounded-full p-2 z-10 transition-colors"
                    title="Foto anterior"
                  >
                    <ChevronLeft className="w-7 h-7" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateMediaGallery(1)}
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/35 text-white rounded-full p-2 z-10 transition-colors"
                    title="Próxima foto"
                  >
                    <ChevronRight className="w-7 h-7" />
                  </button>
                </>
              )}
              
              {viewingMedia.type === 'photo' ? (
                <img
                  src={viewingMedia.url || viewingMedia.dataUrl || viewingMedia.thumbnailUrl || (typeof viewingMedia === 'string' ? viewingMedia : '')}
                  alt="Foto"
                  className="max-w-full max-h-[90vh] mx-auto object-contain rounded-lg"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', viewingMedia);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <video
                  src={viewingMedia.url || viewingMedia.dataUrl || viewingMedia.thumbnailUrl || (typeof viewingMedia === 'string' ? viewingMedia : '')}
                  controls
                  autoPlay
                  className="max-w-full max-h-[90vh] mx-auto rounded-lg"
                  onError={(e) => {
                    console.error('Erro ao carregar vídeo:', viewingMedia);
                  }}
                >
                  Seu navegador não suporta a reprodução de vídeos.
                </video>
              )}
              
              <div className="mt-3 text-center text-white space-y-1">
                {mediaGallery.length > 1 && (
                  <p className="text-sm text-white/90">
                    {mediaGalleryIndex + 1} / {mediaGallery.length}
                  </p>
                )}
                {viewingMedia.name && (
                  <p className="text-sm">{viewingMedia.name}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Decisão Final do Recebedor (após resposta da Qualidade) */}
        {showFinalDecisionModal && receivingForFinalDecision && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Decisão Final - Recebimento</h2>
                <p className="text-sm text-gray-600 mt-1">{receivingForFinalDecision.produto_nome}</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Informações do Recebimento */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div><span className="font-medium">Fornecedor:</span> {receivingForFinalDecision.fornecedor}</div>
                  <div>
                    <span className="font-medium">Lote(s):</span> {formatarLotesParaExibicao(
                      Array.isArray(receivingForFinalDecision.lote_fornecedor) 
                        ? receivingForFinalDecision.lote_fornecedor 
                        : receivingForFinalDecision.lote_fornecedor
                    )}
                  </div>
                  <div><span className="font-medium">Quantidade:</span> {formatQuantidadeComUnidade(receivingForFinalDecision.quantidade_recebida, receivingForFinalDecision.unidade_medida)}</div>
                </div>

                {/* Decisão da Qualidade */}
                <div className={`p-4 rounded-lg border-l-4 ${
                  receivingForFinalDecision.decisao_final === 'aprovado_qualidade' 
                    ? 'bg-green-50 border-green-500' 
                    : 'bg-red-50 border-red-500'
                }`}>
                  <p className="font-medium text-gray-900">
                    Decisão da Qualidade: 
                    <span className={receivingForFinalDecision.decisao_final === 'aprovado_qualidade' ? 'text-green-700' : 'text-red-700'}>
                      {' '}{receivingForFinalDecision.decisao_final === 'aprovado_qualidade' ? '✓ APROVADO' : '✗ REJEITADO'}
                    </span>
                  </p>
                  {receivingForFinalDecision.decisao_qualidade?.observacoes && (
                    <p className="text-sm text-gray-700 mt-2">
                      <span className="font-medium">Observações:</span> {receivingForFinalDecision.decisao_qualidade.observacoes}
                    </p>
                  )}
                </div>

                {/* Decisão Final do Recebedor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Sua Decisão Final: *
                  </label>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {receivingForFinalDecision.decisao_final === 'aprovado_qualidade' && (
                      <button
                        onClick={() => setFinalDecision('aceitar')}
                        disabled={loading}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          finalDecision === 'aceitar'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${
                          finalDecision === 'aceitar' ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <div className="font-medium text-gray-900">Aceitar</div>
                        <div className="text-xs text-gray-600 mt-1">Enviar para estoque</div>
                      </button>
                    )}

                    {receivingForFinalDecision.decisao_final === 'rejeitado_qualidade' && (
                      <button
                        onClick={() => setFinalDecision('em_analise_qualidade')}
                        disabled={loading}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          finalDecision === 'em_analise_qualidade'
                            ? 'border-yellow-500 bg-yellow-50'
                            : 'border-gray-200 hover:border-yellow-300'
                        }`}
                      >
                        <Clock className={`w-8 h-8 mx-auto mb-2 ${
                          finalDecision === 'em_analise_qualidade' ? 'text-yellow-600' : 'text-gray-400'
                        }`} />
                        <div className="font-medium text-gray-900">Analisar</div>
                        <div className="text-xs text-gray-600 mt-1">Enviar para análise</div>
                      </button>
                    )}

                    <button
                      onClick={() => setFinalDecision('rejeitar')}
                      disabled={loading}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        finalDecision === 'rejeitar'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-red-300'
                      } ${receivingForFinalDecision.decisao_final === 'aprovado_qualidade' ? '' : ''}`}
                    >
                      <XCircle className={`w-8 h-8 mx-auto mb-2 ${
                        finalDecision === 'rejeitar' ? 'text-red-600' : 'text-gray-400'
                      }`} />
                      <div className="font-medium text-gray-900">Devolver</div>
                      <div className="text-xs text-gray-600 mt-1">Devolver ao fornecedor</div>
                    </button>
                  </div>
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observações Adicionais
                    {(finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade') && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                    {finalDecision === 'aceitar' && (
                      <span className="text-gray-500 text-xs font-normal ml-1">(Opcional)</span>
                    )}
                  </label>
                  <textarea
                    value={finalObservations}
                    onChange={(e) => setFinalObservations(e.target.value)}
                    rows={3}
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      (finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade') && !finalObservations.trim()
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder={
                      (finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade')
                        ? 'Observações obrigatórias sobre a decisão...'
                        : 'Observações sobre a decisão final...'
                    }
                  />
                  {(finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade') && !finalObservations.trim() && (
                    <p className="text-red-500 text-xs mt-1">Este campo é obrigatório para esta decisão</p>
                  )}
                </div>
              </div>

              {/* Botões do Modal */}
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowFinalDecisionModal(false);
                    setReceivingForFinalDecision(null);
                  }}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleFinalDecision}
                  disabled={
                    loading || 
                    !finalDecision || 
                    ((finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade') && !finalObservations.trim())
                  }
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    loading || 
                    !finalDecision || 
                    ((finalDecision === 'rejeitar' || finalDecision === 'em_analise_qualidade') && !finalObservations.trim())
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Processando...' : 'Confirmar Decisão'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {palletRecebimento && (
        <PalletsRecebimentoModal
          recebimento={palletRecebimento}
          empresaId={palletRecebimento.companyId || palletRecebimento.empresa_id || companyId || getEmpresaId()}
          onClose={() => setPalletRecebimento(null)}
        />
      )}
    </div>
  );
};

export default ReceivingsPage;

